<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      content-class="pw-refund-form"
      max-width="72%"
      :scrim="false"
      transition="dialog-bottom-transition"
      @keydown.esc="dialog = false"
    >
      <template v-slot:activator="{ on, attrs }">
        <span
          v-if="externalLabel"
          v-bind="attrs"
          v-on="on"
          :style="
            `color: ${buttonTextColor}; cursor: pointer; font-weight: 600; text-decoration: underline;`
          "
          >{{ externalLabel }}</span
        >
        <v-btn
          v-else
          x-small
          fab
          :color="buttonColor"
          class="white--text font-weight-bold"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon>mdi-eye</v-icon>
        </v-btn>
      </template>
      <v-card>
        <div
          class="d-flex flex-row justify-space-between pa-4 bg-grey"
          color="primary"
          name="refund-mainteiner"
        >
          <v-btn icon dark @click="dialog = false"> </v-btn>
          <span class="text-h6 font-weight-bold d-flex align-center">
            <v-icon left color="#1B1D21">mdi-file-document-outline</v-icon>
            Solicitud de reembolso N°
            {{ this.refundData == null ? '-' : this.refundData.refund.referenceIndex }}
            <v-chip v-if="isUsersFirstRefund()" small outlined color="var(--pw-info)" class="ml-3"
              >Primer reembolso</v-chip
            >
          </span>
          <v-btn icon dark @click="dialog = false">
            <v-icon color="#1B1D21">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider color="#EAEEF4"></v-divider>

        <!-- [NUEVO] Alerta Lista Negra -->
        <div v-if="canSeeFraud && blacklistInfo" class="blacklist-alert-refund">
          <div class="blacklist-alert-header">
            <v-icon color="white" size="20" class="mr-2">mdi-cancel</v-icon>
            <span class="blacklist-title">USUARIO EN LISTA NEGRA</span>
          </div>
          <div v-if="blacklistInfo.reason" class="blacklist-reason">
            {{ blacklistInfo.reason }}
          </div>
        </div>

        <!-- [NUEVO] Alertas de sospecha — usuario y clínica por separado -->
        <div v-if="canSeeFraud && suspiciousUserInfo" class="suspicious-alert-refund">
          <div class="suspicious-alert-header">
            <v-icon color="white" size="20" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span class="suspicious-title">USUARIO SOSPECHOSO</span>
          </div>
          <div v-if="suspiciousUserInfo.reason" class="suspicious-reason-refund">
            {{ suspiciousUserInfo.reason }}
          </div>
        </div>
        <div v-if="canSeeFraud && suspiciousClinicInfo" class="suspicious-alert-refund">
          <div class="suspicious-alert-header">
            <v-icon color="white" size="20" class="mr-2">mdi-alert-circle-outline</v-icon>
            <span class="suspicious-title">CLÍNICA SOSPECHOSA</span>
          </div>
          <div v-if="suspiciousClinicInfo.reason" class="suspicious-reason-refund">
            {{ suspiciousClinicInfo.reason }}
          </div>
        </div>

        <!-- [NUEVO] Marcar como sospechoso — usuario y clínica por separado (019-mark-suspicious-entities-ui, US4/US5) -->
        <!-- No depende de la resolución elegida para el reembolso -->
        <div v-if="canFlagSuspicious" class="d-flex flex-wrap pa-4 pb-0" style="gap: 8px;">
          <v-btn
            small
            outlined
            color="orange darken-3"
            @click="showSuspiciousUserModal = true"
          >
            <v-icon left small>mdi-alert-circle-outline</v-icon>
            Marcar usuario como sospechoso
          </v-btn>
          <v-btn
            small
            outlined
            color="orange darken-3"
            :disabled="!(dataRut && dataRut.sellerRut)"
            @click="showSuspiciousClinicModal = true"
          >
            <v-icon left small>mdi-alert-circle-outline</v-icon>
            Marcar clínica como sospechosa
          </v-btn>
        </div>

        <!-- ! -->
        <v-list lines="two" subheader class="pa-4" style="background: #F8FAFC;">
          <!-- Container -->
          <!-- Sección de Tarjetas de Información -->
          <v-container fluid>
            <!-- Fila 1: Mascota | Tutor(es) | Membresía -->
            <v-row>
              <!-- Tarjeta de Mascota -->
              <v-col cols="12" md="4" class="d-flex">
                <info-card
                  v-if="petData"
                  title="Mascota"
                  :items="petInfoItems"
                  :gridItems="petGridItems"
                  :gridItems2="petGridItems2"
                  :gridColumns2="3"
                  icon="mdi-paw"
                  :avatarImage="petImageUrl"
                  :avatarIcon="
                    !petImageUrl ? (petData.species === 'dog' ? 'mdi-dog' : 'mdi-cat') : null
                  "
                  class="flex-grow-1"
                ></info-card>
                <v-skeleton-loader
                  v-else-if="loadingOverlay"
                  type="card"
                  class="flex-grow-1"
                  style="border-radius:12px;"
                ></v-skeleton-loader>
                <v-alert v-else type="warning" outlined>Sin información de mascota</v-alert>
              </v-col>

              <!-- Tarjeta de Tutor(es) -->
              <v-col cols="12" md="4" class="d-flex">
                <info-card
                  v-if="user"
                  :title="user.isVip ? 'Tutor VIP' : 'Tutor(es)'"
                  :items="userInfoItems"
                  icon="mdi-account"
                  :isVip="user.isVip || false"
                  class="flex-grow-1"
                ></info-card>
                <v-skeleton-loader
                  v-else-if="loadingOverlay"
                  type="card"
                  class="flex-grow-1"
                  style="border-radius:12px;"
                ></v-skeleton-loader>
              </v-col>

              <!-- Tarjeta de Membresía -->
              <v-col cols="12" md="4" class="d-flex">
                <info-card
                  v-if="planData"
                  title="Membresía"
                  :items="membershipInfoItems"
                  :gridItems2="membershipGridItems"
                  icon="mdi-card-account-details-outline"
                  class="flex-grow-1"
                ></info-card>
                <v-skeleton-loader
                  v-else-if="loadingOverlay"
                  type="card"
                  class="flex-grow-1"
                  style="border-radius:12px;"
                ></v-skeleton-loader>
                <v-alert v-else type="warning" outlined>Sin información de membresía</v-alert>
              </v-col>
            </v-row>

            <!-- Fila 2: Servicio | Adjuntos | Don Graff -->
            <v-row>
              <!-- Tarjeta de Servicio -->
              <v-col v-if="canSeeAmounts" cols="12" md="4" class="d-flex">
                <info-card
                  v-if="service"
                  :title="service.name"
                  :items="serviceInfoItems"
                  :gridItems2="serviceCombinedGridItems"
                  class="flex-grow-1"
                ></info-card>
                <v-skeleton-loader
                  v-else-if="loadingOverlay"
                  type="card"
                  class="flex-grow-1"
                  style="border-radius:12px;"
                ></v-skeleton-loader>
                <v-alert v-else type="warning" outlined>Sin información del servicio</v-alert>
              </v-col>

              <!-- Tarjeta de Adjuntos -->
              <v-col v-if="canSeeAttachments || canSeeBankData" cols="12" md="4" class="d-flex">
                <info-card
                  v-if="(canSeeAttachments && files) || bankDataItems.length"
                  :title="canSeeAttachments ? 'Adjuntos' : 'Datos bancarios'"
                  :items="attachmentsInfoItems"
                  :gridItems2="attachmentsGridItems2"
                  icon="mdi-paperclip"
                  class="flex-grow-1"
                ></info-card>
                <v-skeleton-loader
                  v-else-if="loadingOverlay"
                  type="card"
                  class="flex-grow-1"
                  style="border-radius:12px;"
                ></v-skeleton-loader>
                <v-alert v-else type="warning" outlined>Sin adjuntos </v-alert>
              </v-col>

              <!-- Tarjeta Don Graff -->
              <v-col v-if="canSeeFraud" cols="12" md="4" class="d-flex">
                <v-card
                  class="fill-height flex-grow-1 elevation-0"
                  style="border-radius: 12px; border: 1px solid #E5E7EB; box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04);"
                  color="white"
                >
                  <v-card-title style="display: flex; align-items: center;">
                    <v-icon left color="primary">mdi-dog</v-icon>
                    <span style="color: #104b94; font-weight: 600; font-size: 16px;"
                      >Insights de Don Graff</span
                    >
                  </v-card-title>
                  <v-divider style="border-color: #E5E7EB;"></v-divider>
                  <v-card-text>
                    <!-- Franja de riesgo (RiskScore) -->
                    <div v-if="donGrafRiskLevelMeta" style="margin-bottom: 12px;">
                      <v-chip
                        :color="donGrafRiskLevelMeta.color"
                        dark
                        label
                        style="font-weight: 600; font-size: 13px;"
                      >
                        {{ riskScoreResult.score }} · {{ donGrafRiskLevelMeta.label }}
                      </v-chip>
                      <div
                        v-if="donGrafRiskSignals.length"
                        style="margin-top: 8px; display: flex; flex-wrap: wrap; gap: 4px;"
                      >
                        <v-chip
                          v-for="signal in donGrafRiskSignals"
                          :key="signal.code"
                          x-small
                          outlined
                          :color="donGrafRiskLevelMeta.color"
                          label
                          >{{ signal.label }}</v-chip
                        >
                      </div>
                    </div>

                    <p
                      v-if="service && service.lastServiceDate"
                      style="font-size: 14px; color: #111827; font-weight: 400; margin-bottom: 12px;"
                    >
                      La mascota tuvo una atención del tipo
                      <strong>{{ lastServiceTypeLabel }}</strong> el
                      <strong>{{ service.lastServiceDate }}</strong
                      >.
                    </p>

                    <!-- Documentos (duplicados + integridad, correlacionados por hash) -->
                    <template v-if="donGrafDocuments.length">
                      <div v-if="donGrafDuplicateCount > 0" style="margin-bottom: 8px;">
                        <v-chip x-small outlined color="error" label>
                          {{ donGrafDuplicateCount }} documento{{
                            donGrafDuplicateCount === 1 ? '' : 's'
                          }}
                          duplicado{{ donGrafDuplicateCount === 1 ? '' : 's' }}
                        </v-chip>
                      </div>
                      <div
                        v-for="doc in donGrafDocuments"
                        :key="doc.attachmentId"
                        style="margin-bottom: 6px;"
                      >
                        <!-- Con hallazgos reales: acordeón desplegable -->
                        <v-expansion-panels v-if="doc.hasFindings" flat>
                          <v-expansion-panel
                            style="border: 1px solid #E5E7EB; border-radius: 8px !important;"
                          >
                            <v-expansion-panel-header style="min-height: 0; padding: 8px 12px;">
                              <div style="font-size: 13px; color: #374151;">
                                <div style="display: flex; align-items: center; gap: 8px;">
                                  <!-- Con hallazgos: siempre warning naranja, nunca el check verde -->
                                  <v-icon size="16" color="orange darken-1">mdi-alert</v-icon>
                                  <strong>{{ doc.type }}</strong>
                                </div>
                                <div
                                  v-if="
                                    doc.status === 'duplicate' &&
                                      doc.usedInReference &&
                                      doc.usedInReference.length
                                  "
                                  style="margin-top: 4px; margin-left: 24px; display: flex; flex-wrap: wrap; gap: 4px;"
                                >
                                  <v-chip
                                    v-for="refId in doc.usedInReference"
                                    :key="refId"
                                    x-small
                                    color="error"
                                    text-color="white"
                                    label
                                    :title="`Abrir reembolso #${refId}`"
                                    @click.stop="openDuplicateRefund(refId)"
                                    >#{{ refId }}</v-chip
                                  >
                                </div>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content style="font-size: 13px; color: #374151;">
                              <ul style="margin: 0; padding-left: 16px;">
                                <li v-for="(line, idx) in doc.integrityView.lines" :key="idx">
                                  {{ line }}
                                </li>
                              </ul>

                              <!-- Detalle técnico (EXIF/C2PA) que respalda los hallazgos -->
                              <div
                                v-if="doc.integrityDetails"
                                style="margin-top: 10px; padding: 14px; background-color: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 8px;"
                              >
                                <div
                                  style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #9CA3AF; font-weight: 600; margin-bottom: 8px;"
                                >
                                  Análisis Metadata
                                </div>
                                <div
                                  v-if="!doc.integrityDetails.exif.evaluable"
                                  style="font-size: 12px; color: #9CA3AF; margin-bottom: 12px;"
                                >
                                  No se pudo evaluar
                                </div>
                                <div
                                  v-else
                                  style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px; margin-bottom: 12px;"
                                >
                                  <div v-for="row in doc.integrityDetails.exif.rows" :key="row.label">
                                    <div class="dj-label">{{ row.label }}</div>
                                    <div class="dj-value">{{ row.value }}</div>
                                  </div>
                                </div>

                                <div
                                  style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #9CA3AF; font-weight: 600; margin-bottom: 8px;"
                                >
                                  Registros de IA
                                </div>
                                <div
                                  v-if="!doc.integrityDetails.c2pa.evaluable"
                                  style="font-size: 12px; color: #9CA3AF;"
                                >
                                  No se pudo evaluar
                                </div>
                                <div v-else style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px 16px;">
                                  <div v-for="row in doc.integrityDetails.c2pa.rows" :key="row.label">
                                    <div class="dj-label">{{ row.label }}</div>
                                    <div class="dj-value">{{ row.value }}</div>
                                  </div>
                                </div>
                              </div>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>

                        <!-- Sin hallazgos: fila simple, sin acordeón -->
                        <div
                          v-else
                          style="border: 1px solid #E5E7EB; border-radius: 8px; padding: 8px 12px;"
                        >
                          <div style="font-size: 13px; color: #374151;">
                            <div style="display: flex; align-items: center; gap: 8px;">
                              <v-icon
                                size="16"
                                :color="
                                  doc.status === 'duplicate'
                                    ? 'error'
                                    : doc.status === 'error'
                                    ? 'grey'
                                    : 'success'
                                "
                              >
                                {{
                                  doc.status === 'duplicate'
                                    ? 'mdi-alert-circle'
                                    : doc.status === 'error'
                                    ? 'mdi-alert'
                                    : 'mdi-check-circle'
                                }}
                              </v-icon>
                              <strong>{{ doc.type }}</strong>
                            </div>
                            <div
                              v-if="
                                doc.status === 'duplicate' &&
                                  doc.usedInReference &&
                                  doc.usedInReference.length
                              "
                              style="margin-top: 4px; margin-left: 24px; display: flex; flex-wrap: wrap; gap: 4px;"
                            >
                              <v-chip
                                v-for="refId in doc.usedInReference"
                                :key="refId"
                                x-small
                                color="error"
                                text-color="white"
                                label
                                :title="`Abrir reembolso #${refId}`"
                                @click.stop="openDuplicateRefund(refId)"
                                >#{{ refId }}</v-chip
                              >
                            </div>
                            <div
                              v-if="doc.status === 'error'"
                              style="margin-top: 4px; margin-left: 24px; color: #9CA3AF;"
                            >
                              No se pudo evaluar este documento.
                            </div>
                            <div
                              v-else-if="doc.integrityView && doc.integrityView.kind === 'clean'"
                              style="margin-top: 4px; margin-left: 24px; color: #2E7D32;"
                            >
                              Sin hallazgos de integridad
                            </div>
                            <div
                              v-else-if="doc.integrityView && doc.integrityView.kind === 'unevaluated'"
                              style="margin-top: 4px; margin-left: 24px; color: #9CA3AF;"
                            >
                              No se pudo evaluar
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Declaración Jurada Digital -->
            <v-row v-if="canSeeClinical && declaracionJurada" class="mt-3">
              <v-col cols="12">
                <v-card
                  class="fill-height elevation-0 d-flex flex-column"
                  style="border-radius: 12px; border: 1px solid #E5E7EB; box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04);"
                  color="white"
                >
                  <v-card-title class="info-card-title">
                    <v-icon left color="primary">mdi-file-document-edit-outline</v-icon>
                    <span style="color: #104b94; font-weight: 600;"
                      >Declaración Jurada Digital</span
                    >
                  </v-card-title>
                  <v-divider style="border-color: #E5E7EB;"></v-divider>
                  <v-card-text>
                    <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 0 16px;">
                      <!-- Título: Información de la clínica -->
                      <div
                        style="grid-column: 1 / -1; font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #9CA3AF; font-weight: 600; margin-bottom: 12px;"
                      >
                        Información de la clínica
                      </div>

                      <!-- Col 1: Fechas -->
                      <div>
                        <div v-if="declaracionJurada.symptomsDate" style="margin-bottom: 12px;">
                          <div class="dj-label">Fecha inicio síntomas</div>
                          <div class="dj-value">{{ declaracionJurada.symptomsDate }}</div>
                        </div>
                        <div v-if="declaracionJurada.clinicDate" style="margin-bottom: 12px;">
                          <div class="dj-label">Fecha atención clínica</div>
                          <div class="dj-value">{{ declaracionJurada.clinicDate }}</div>
                        </div>
                      </div>
                      <!-- Col 2: Clínica -->
                      <div>
                        <div v-if="declaracionJurada.clinicName" style="margin-bottom: 12px;">
                          <div class="dj-label">Nombre de la clínica</div>
                          <div class="dj-value">{{ declaracionJurada.clinicName }}</div>
                        </div>
                        <div v-if="declaracionJurada.clinicEmail" style="margin-bottom: 12px;">
                          <div class="dj-label">Email de la clínica</div>
                          <div class="dj-value">{{ declaracionJurada.clinicEmail }}</div>
                        </div>
                        <div v-if="declaracionJurada.clinicPhone" style="margin-bottom: 12px;">
                          <div class="dj-label">Teléfono de la clínica</div>
                          <div class="dj-value">{{ declaracionJurada.clinicPhone }}</div>
                        </div>
                      </div>
                      <!-- Col 3: Veterinario -->
                      <div>
                        <div v-if="declaracionJurada.vetName" style="margin-bottom: 12px;">
                          <div class="dj-label">Nombre del veterinario</div>
                          <div class="dj-value">{{ declaracionJurada.vetName }}</div>
                        </div>
                        <div v-if="declaracionJurada.vetRut" style="margin-bottom: 12px;">
                          <div class="dj-label">RUT del veterinario</div>
                          <div class="dj-value">{{ declaracionJurada.vetRut }}</div>
                        </div>
                      </div>

                      <!-- Título: Detalles clínicos -->
                      <div
                        style="grid-column: 1 / -1; font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #9CA3AF; font-weight: 600; margin-top: 8px; margin-bottom: 12px;"
                      >
                        Detalles clínicos
                      </div>

                      <!-- Col 1 -->
                      <div>
                        <div v-if="declaracionJurada.incidentType" style="margin-bottom: 12px;">
                          <div class="dj-label">Tipo de incidente</div>
                          <div class="dj-value">{{ declaracionJurada.incidentType }}</div>
                        </div>
                      </div>
                      <!-- Col 2 -->
                      <div>
                        <div v-if="declaracionJurada.lapso" style="margin-bottom: 12px;">
                          <div class="dj-label">Lapso</div>
                          <div class="dj-value">{{ declaracionJurada.lapso }}</div>
                        </div>
                      </div>
                      <!-- Col 3 -->
                      <div>
                        <div v-if="declaracionJurada.estadoMascota" style="margin-bottom: 12px;">
                          <div class="dj-label">Estado de la mascota</div>
                          <div class="dj-value">
                            {{ declaracionJurada.estadoMascota
                            }}{{
                              declaracionJurada.estadoOtro
                                ? `: ${declaracionJurada.estadoOtro}`
                                : ''
                            }}
                          </div>
                        </div>
                      </div>

                      <!-- Signos clínicos: ancho completo -->
                      <div
                        v-if="declaracionJurada.signos && declaracionJurada.signos.length"
                        style="grid-column: 1 / -1; margin-bottom: 12px;"
                      >
                        <div class="dj-label">Signos clínicos</div>
                        <div class="dj-value">
                          {{ declaracionJurada.signos.join(', ')
                          }}{{
                            declaracionJurada.signosOtros
                              ? `: ${declaracionJurada.signosOtros}`
                              : ''
                          }}
                        </div>
                      </div>
                      <!-- Prestaciones: ancho completo -->
                      <div
                        v-if="
                          declaracionJurada.prestaciones && declaracionJurada.prestaciones.length
                        "
                        style="grid-column: 1 / -1; margin-bottom: 12px;"
                      >
                        <div class="dj-label">Prestaciones</div>
                        <div class="dj-value">
                          {{ declaracionJurada.prestaciones.join(', ')
                          }}{{
                            declaracionJurada.prestacionesOtros
                              ? `: ${declaracionJurada.prestacionesOtros}`
                              : ''
                          }}
                        </div>
                      </div>
                      <!-- Cirugía -->
                      <div
                        v-if="declaracionJurada.cirugia"
                        style="grid-column: 1 / -1; margin-bottom: 12px;"
                      >
                        <div class="dj-label">Cirugía</div>
                        <div class="dj-value">{{ declaracionJurada.cirugia }}</div>
                      </div>
                      <!-- Desarrollo del caso -->
                      <div
                        v-if="declaracionJurada.desarrollo"
                        style="grid-column: 1 / -1; margin-bottom: 12px;"
                      >
                        <div class="dj-label">Desarrollo del caso</div>
                        <div class="dj-value" style="white-space: pre-wrap;">
                          {{ declaracionJurada.desarrollo }}
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-list>

        <!-- ! -->
        <v-divider></v-divider>

        <div v-if="can('refund-inbox:review-status')" class="d-flex mt-6 justify-center">
          <span class="text-h6 font-weight-bold">Cambiar a: </span>
        </div>

        <v-list lines="two" subheader class="pa-4">
          <div class="d-flex flex-row justify-center">
            <!-- ACCIONES -->
            <div class="d-flex flex-column align-center">
              <div class="d-flex flex-row">
                <v-form @submit.prevent="watchData" class="d-flex flex-column">
                  <v-radio-group v-if="can('refund-inbox:review-status')" v-model="selectedOption" row>
                    <v-radio
                      v-for="status in visibleStatuses"
                      :key="status.value"
                      :value="status"
                      :disabled="!!optionDisabledReason(status)"
                      :color="refundStatusColor(status.value)"
                      class="custom-font"
                      style="font-weight: 300;"
                      dense
                    >
                      <template #label>
                        <span class="status-option">
                          {{ status.text }}
                          <span v-if="optionDisabledReason(status)" class="status-option__hint">
                            {{ optionDisabledReason(status) }}
                          </span>
                        </span>
                      </template>
                    </v-radio>
                  </v-radio-group>

                  <!-- Mensaje de consejo -->
                  <p
                    v-if="selectedOption.value != null"
                    style="font-size: 12px; font-weight: 300"
                    class="text-center custom-font"
                  >
                    {{ selectedOption.adviceMsg }}
                  </p>

                  <!-- Fecha de pago (solo cuando se selecciona Pagado) -->
                  <div
                    v-if="selectedOption.value === 500"
                    style="padding-left: 16px; padding-right: 16px; padding-bottom: 12px;"
                  >
                    <v-text-field
                      v-model="paidAt"
                      label="Fecha de pago"
                      type="date"
                      outlined
                      dense
                      hide-details
                      style="font-size: 14px;"
                    />
                  </div>

                  <!-- Respuesta para reembolso -->
                  <div
                    v-if="respuestasTipoFiltradas.length > 0"
                    style="padding-left: 16px; padding-right: 16px; padding-bottom: 8px;"
                  >
                    <v-select
                      :items="respuestasTipoFiltradas"
                      item-text="title"
                      item-value="content"
                      label="Respuesta para reembolso (opcional)"
                      outlined
                      dense
                      hide-details
                      clearable
                      style="font-size: 14px;"
                      @change="
                        val => {
                          if (val) inputObservations = val;
                        }
                      "
                      @click:clear="() => {}"
                    />
                  </div>

                  <!-- Campo de Observaciones: va al cambio de estado -->
                  <div
                    v-if="can('refund-inbox:review-status')"
                    style="border-radius: 8px; padding-left: 16px; padding-right: 16px; padding-top: 8px; padding-bottom: 8px; margin-bottom: 12px"
                  >
                    <v-textarea
                      class="custom-placeholder"
                      style="margin: 0px; font-size: 14px;font-weight: 300;border: 1px solid #abafb1;border-radius: 8px; padding: 16px;"
                      v-model="inputObservations"
                      hide-details
                      placeholder="Observaciones"
                      maxlength="300"
                      rows="4"
                    />
                  </div>
                </v-form>
              </div>
            </div>
          </div>
        </v-list>
        <!-- ! -->
        <v-divider></v-divider>
        <v-list lines="two" subheader>
          <v-expansion-panels v-model="activePanel" flat class="pa-4">
            <v-expansion-panel elevation="0">
              <v-expansion-panel-header>
                <v-row>
                  <span class="text-h6" style="font-weight: 600;">Historial de cambios</span>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card class="elevation-0">
                  <v-card-text>
                    <template
                      v-if="changelogs.length > 0"
                      class="d-flex flex-column justify-center align-center"
                    >
                      <div class="d-flex flex-column align-center">
                        <p class="font-weight-bold text-center">Más reciente</p>
                      </div>
                      <v-timeline>
                        <v-timeline-item
                          v-for="changelog in changelogs"
                          :key="changelog.id"
                          :color="changelog.status.highlightColor"
                        >
                          <div class="d-flex flex-column align-center">
                            <div class="d-flex flex-column align-center pr-2">
                              <div class="font-weight-medium">
                                {{ changelog.status.name }}&nbsp;
                              </div>

                              <span style="font-size: 12px"
                                >({{ parseDate(new Date(changelog.createdAt)) }} -
                                {{ new Date(changelog.createdAt).getHours() }}:{{
                                  new Date(changelog.createdAt).getMinutes()
                                }})</span
                              >
                            </div>

                            <div>
                              <div
                                v-if="changelog.observations != null"
                                class="d-flex flex-column align-center"
                              >
                                <span style="font-size: 14px"><u>Observaciones:</u></span>
                                <p style="font-size: 13px; text-align:center">
                                  "{{ changelog.observations || 'Sin observaciones' }}"
                                </p>
                              </div>

                              <div v-else>
                                <p style="font-size: 12px">
                                  Sin observaciones
                                </p>
                              </div>
                            </div>
                          </div>
                        </v-timeline-item>
                      </v-timeline>
                      <p class="font-weight-bold text-center">Más antigua</p>
                    </template>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-divider></v-divider>
          <!-- Mostrar formulario interno solo si el estado seleccionado es "Rechazar" -->

          <div class="px-5 py-5" v-if="selectedOption.value === 300">
            <v-container>
              <!-- SECCIÓN 1: Detección de fraude -->
              <v-row>
                <v-col cols="12">
                  <h3 class="font-weight-bold">Detección de fraude</h3>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col cols="12" md="4">
                  <p style="font-size: 12px; color: #6A7682; margin-bottom: 4px; font-weight: 500;">
                    ¿Es un fraude?
                  </p>
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <span style="font-size: 14px; color: #6A7682;">No</span>
                    <v-switch
                      v-model="isFraudulent"
                      color="red"
                      hide-details
                      class="ma-0 pa-0"
                      style="display: inline-flex;"
                    ></v-switch>
                    <span style="font-size: 14px; color: #6A7682;">Sí</span>
                  </div>
                </v-col>

                <v-col cols="12" md="8" v-if="isFraudulent">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    Clasificación del fraude
                    <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <v-text-field
                      v-model="fraudClassification"
                      flat
                      solo
                      hide-details
                      single-line
                      placeholder="Ej: Documentación falsificada"
                      class="custom-placeholder"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                    />
                  </div>
                </v-col>
              </v-row>

              <!-- SECCIÓN 2: Registro interno -->
              <v-row class="mt-6">
                <v-col cols="12">
                  <h3 class="font-weight-bold">Registro interno</h3>
                </v-col>
              </v-row>

              <!-- Fila 1: Fecha + Anotaciones -->
              <v-row>
                <v-col cols="12" md="6">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    ¿Cuándo fue usado?
                    <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <v-menu
                    v-model="menuRefundApprovedDate"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 0 16px; height: 48px; display: flex; align-items: center;"
                      >
                        <div class="d-flex align-center" style="width: 100%;">
                          <div style="margin-right: 20px;">
                            <CalendarIcon />
                          </div>
                          <div class="flex-row" style="flex: 1;">
                            <v-text-field
                              v-model="refundApprovedDate"
                              readonly
                              placeholder="Selecciona una fecha"
                              hide-details
                              class="custom-placeholder"
                              v-bind="attrs"
                              v-on="on"
                              style="margin: 0; padding: 0; font-size: 14px;"
                            ></v-text-field>
                          </div>
                        </div>
                      </div>
                    </template>
                    <v-date-picker
                      v-model="refundApprovedDate"
                      locale="es"
                      @input="menuRefundApprovedDate = false"
                      scrollable
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <v-col cols="12" md="6">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    Anotaciones para Pawer
                    <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <v-text-field
                      v-model="refundAnnotations"
                      flat
                      solo
                      hide-details
                      single-line
                      placeholder="Anotaciones internas"
                      class="custom-placeholder"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                    />
                  </div>
                </v-col>
              </v-row>

              <!-- Fila 2: Gasto + Monto + Deducible -->
              <v-row class="mt-4">
                <v-col cols="12" md="4">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    Gasto del usuario
                    <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <v-text-field
                      v-model="userExpenseFormatted"
                      flat
                      solo
                      single-line
                      hide-details
                      :placeholder="`${currencySymbol} Gasto del usuario`"
                      class="custom-placeholder"
                      type="text"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                    />
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    Monto a reembolsar
                    <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <v-text-field
                      v-model="refundAmountFormatted"
                      flat
                      solo
                      single-line
                      hide-details="auto"
                      :rules="[refundAmountCapRule]"
                      :placeholder="`${currencySymbol} Monto a reembolsar`"
                      class="custom-placeholder"
                      type="text"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                    />
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    Deducible del servicio
                  </p>
                  <div
                    :style="
                      service && service.deductible === 'No aplica'
                        ? 'border-radius: 8px; background-color: #F5F5F5; padding: 4px 16px;'
                        : 'border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;'
                    "
                  >
                    <v-text-field
                      v-model="serviceDeductibleAppliedFormatted"
                      flat
                      solo
                      hide-details
                      single-line
                      :placeholder="`${currencySymbol} Deducible aplicado`"
                      :disabled="service && service.deductible === 'No aplica'"
                      class="custom-placeholder"
                      type="text"
                      style="margin: 0px; padding: 5px 0; font-size: 14px; background-color: transparent;"
                    />
                  </div>
                </v-col>
              </v-row>

              <!-- Fila 3: Suceso + Tipo de lesión + Detalle clínico -->
              <v-row class="mt-4">
                <v-col cols="12" md="4">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    Suceso <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <v-text-field
                      v-model="internalCircumstance"
                      flat
                      solo
                      hide-details
                      single-line
                      placeholder="Describe el suceso"
                      class="custom-placeholder"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                    />
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    Tipo de lesión <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <v-text-field
                      v-model="internalInjuryType"
                      flat
                      solo
                      hide-details
                      single-line
                      placeholder="Tipo de lesión o diagnóstico"
                      class="custom-placeholder"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                    />
                  </div>
                </v-col>

                <v-col cols="12" md="4">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    Detalle clínico
                    <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <v-text-field
                      v-model="internalEventDescription"
                      flat
                      solo
                      hide-details
                      single-line
                      placeholder="Detalle del evento clínico"
                      class="custom-placeholder"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                    />
                  </div>
                </v-col>
              </v-row>

              <!-- Fila 4: Clínica (md=3) + Switch clínica (md=3) -->
              <v-row class="mt-4">
                <v-col cols="12" md="3" style="position: relative;">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    Clínica veterinaria
                    <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <!-- Lista con los nombres encontrados mientras se escribe (incluye la razón
                         social si la clínica tiene dos, 072); se puede escribir uno nuevo. -->
                    <v-combobox
                      v-model="internalClinicName"
                      :search-input.sync="clinicNameSearchInput"
                      :items="clinicNameOptions"
                      :loading="clinicSearchLoading"
                      no-filter
                      flat
                      solo
                      hide-details
                      single-line
                      placeholder="Nombre de la clínica"
                      class="custom-placeholder"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                      autocomplete="off"
                      @change="onClinicNameCommit"
                    >
                      <template v-slot:no-data>
                        <v-list-item dense>
                          <v-list-item-subtitle>{{ clinicNameNoDataText }}</v-list-item-subtitle>
                        </v-list-item>
                      </template>
                    </v-combobox>
                  </div>
                  <!-- 072-clinic-rut-multiple-names: classify a genuinely new second name -->
                  <v-radio-group
                    v-if="needsClinicNameTypeSelection"
                    v-model="internalClinicNameType"
                    row
                    hide-details
                    style="margin: 4px 0 0 0;"
                  >
                    <v-radio label="Nombre de clínica" value="trade" />
                    <v-radio label="Razón social" value="business" />
                  </v-radio-group>
                </v-col>

                <v-col cols="12" md="3">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    Región / Distrito de clínica
                    <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <v-text-field
                      v-model="internalClinicCounty"
                      flat
                      solo
                      hide-details
                      single-line
                      placeholder="Región o distrito"
                      class="custom-placeholder"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                    />
                  </div>
                </v-col>

                <v-col cols="12" md="3">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    RUT / DNI de clínica
                    <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <!-- RUTs de la clínica elegida, o de las encontradas; se puede escribir uno nuevo. -->
                    <v-combobox
                      v-model="internalClinicRut"
                      :search-input.sync="clinicRutSearchInput"
                      :items="clinicRutOptions"
                      flat
                      solo
                      hide-details
                      single-line
                      placeholder="RUT o DNI"
                      class="custom-placeholder"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                      autocomplete="off"
                      @blur="formatClinicRutField"
                    >
                      <template v-slot:no-data>
                        <v-list-item dense>
                          <v-list-item-subtitle>Elige o busca una clínica para ver sus RUT</v-list-item-subtitle>
                        </v-list-item>
                      </template>
                    </v-combobox>
                  </div>
                </v-col>

                <v-col cols="12" md="3">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    País de clínica
                  </p>
                  <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                    <v-select
                      v-model="internalClinicCountryId"
                      :items="countryOptions"
                      :disabled="clinicCountryLocked"
                      @change="formatClinicRutField"
                      flat
                      solo
                      hide-details
                      single-line
                      placeholder="País"
                      style="margin: 0px; padding: 5px 0; font-size: 14px;"
                    />
                  </div>
                  <p v-if="clinicCountryLocked" style="font-size: 11px; color: #6A7682; margin: 4px 0 0 0;">
                    Se cambia desde el mantenedor de clínicas.
                  </p>
                </v-col>

                <v-col cols="12" md="3">
                  <p style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;">
                    ¿Clínica asociada al fraude?
                  </p>
                  <div style="display: flex; align-items: center; gap: 8px; padding-top: 8px;">
                    <span style="font-size: 14px; color: #6A7682;">No</span>
                    <v-switch
                      v-model="isClinicFraudulent"
                      color="red"
                      hide-details
                      class="ma-0 pa-0"
                      style="display: inline-flex;"
                    ></v-switch>
                    <span style="font-size: 14px; color: #6A7682;">Sí</span>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </div>

          <!-- Registro del reembolso: se llena y guarda en cualquier estado (salvo
               Rechazar, que muestra estos mismos campos en su "Registro interno").
               Antes de aprobar se guarda como borrador, sin registrar uso del
               servicio. Colapsable; abierto por defecto al Aprobar. -->
          <div class="px-5 py-5" v-if="canEditApprovalRecord && showApprovalRecordSection">
            <v-expansion-panels v-model="approvalRecordPanel" flat>
              <v-expansion-panel>
                <v-expansion-panel-header class="px-3">
                  <h3 class="font-weight-bold">Registro aprobado del reembolso</h3>
                </v-expansion-panel-header>
                <v-expansion-panel-content eager>
                  <v-container>
                    <v-form ref="form">
                      <v-row>
                        <!-- Input de fecha -->
                        <v-col cols="12" md="6">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            ¿Cuándo fue usado?
                          </p>
                          <v-menu
                            v-model="menuRefundApprovedDate"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <div
                                style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 0 16px; height: 48px; display: flex; align-items: center;"
                              >
                                <div class="d-flex align-center" style="width: 100%;">
                                  <div style="margin-right: 20px;">
                                    <CalendarIcon />
                                  </div>
                                  <div class="flex-row" style="flex: 1;">
                                    <v-text-field
                                      v-model="refundApprovedDate"
                                      readonly
                                      placeholder="¿Cuándo fue usado?"
                                      :rules="[requiredRules]"
                                      hide-details="auto"
                                      class="custom-placeholder"
                                      v-bind="attrs"
                                      v-on="on"
                                      style="margin: 0; padding: 0; font-size: 14px;"
                                    ></v-text-field>
                                  </div>
                                </div>
                              </div>
                            </template>

                            <v-date-picker
                              v-model="refundApprovedDate"
                              locale="es"
                              @input="menuRefundApprovedDate = false"
                              scrollable
                            ></v-date-picker>
                          </v-menu>
                        </v-col>

                        <!-- Input de anotaciones -->
                        <v-col cols="12" md="6">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            Anotaciones para Pawer
                            <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                          </p>
                          <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                            <v-text-field
                              v-model="refundAnnotations"
                              flat
                              solo
                              hide-details
                              single-line
                              placeholder="Anotaciones para Pawer"
                              class="custom-placeholder"
                              style="margin: 0px; padding-top: 5px; padding-bottom: 5px; font-size: 14px;"
                            />
                          </div>
                        </v-col>
                      </v-row>

                      <v-row>
                        <!-- Input de gasto del usuario -->
                        <v-col cols="12" md="4">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            Gasto del usuario
                          </p>
                          <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                            <v-text-field
                              v-model="userExpenseFormatted"
                              flat
                              solo
                              single-line
                              :placeholder="`${currencySymbol} Gasto del usuario`"
                              class="custom-placeholder"
                              type="text"
                              hide-details="auto"
                              style="margin: 0px; padding-top: 5px; padding-bottom: 5px; font-size: 14px;"
                              :rules="[requiredRules]"
                            />
                          </div>
                        </v-col>

                        <!-- Input de monto reembolsado -->
                        <v-col cols="12" md="4">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            Monto a reembolsar
                          </p>
                          <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                            <v-text-field
                              v-model="refundAmountFormatted"
                              flat
                              solo
                              single-line
                              :placeholder="`${currencySymbol} Monto a reembolsar`"
                              :rules="[requiredRules, refundAmountCapRule]"
                              class="custom-placeholder"
                              type="text"
                              hide-details="auto"
                              style="margin: 0px; padding-top: 5px; padding-bottom: 5px; font-size: 14px;"
                            />
                          </div>
                        </v-col>

                        <!-- Input ServiceDeductible -->
                        <v-col cols="12" md="4">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            Deducible del servicio
                          </p>
                          <div
                            :style="
                              service && service.deductible === 'No aplica'
                                ? 'border-radius: 8px; background-color: #F5F5F5; padding: 4px 16px;'
                                : 'border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;'
                            "
                          >
                            <v-text-field
                              v-model="serviceDeductibleAppliedFormatted"
                              flat
                              solo
                              hide-details
                              single-line
                              :placeholder="`${currencySymbol} Deducible aplicado`"
                              :disabled="service && service.deductible === 'No aplica'"
                              class="custom-placeholder"
                              type="text"
                              style="margin: 0px; padding-top: 5px; padding-bottom: 5px; font-size: 14px; background-color: transparent !important;"
                            />
                          </div>
                        </v-col>
                      </v-row>

                      <v-row class="mt-4">
                        <!-- Suceso -->
                        <v-col cols="12" md="4">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            Suceso <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                          </p>
                          <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                            <v-text-field
                              v-model="internalCircumstance"
                              flat
                              solo
                              hide-details
                              single-line
                              placeholder="Describe el suceso"
                              class="custom-placeholder"
                              style="margin: 0px; padding: 5px 0; font-size: 14px;"
                            />
                          </div>
                        </v-col>

                        <!-- Tipo de lesión -->
                        <v-col cols="12" md="4">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            Tipo de lesión
                            <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                          </p>
                          <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                            <v-text-field
                              v-model="internalInjuryType"
                              flat
                              solo
                              hide-details
                              single-line
                              placeholder="Tipo de lesión o diagnóstico"
                              class="custom-placeholder"
                              style="margin: 0px; padding: 5px 0; font-size: 14px;"
                            />
                          </div>
                        </v-col>

                        <!-- Detalle clínico -->
                        <v-col cols="12" md="4">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            Detalle clínico
                            <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                          </p>
                          <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                            <v-text-field
                              v-model="internalEventDescription"
                              flat
                              solo
                              hide-details
                              single-line
                              placeholder="Detalle del evento clínico"
                              class="custom-placeholder"
                              style="margin: 0px; padding: 5px 0; font-size: 14px;"
                            />
                          </div>
                        </v-col>
                      </v-row>

                      <v-row class="mt-4">
                        <!-- Clínica veterinaria -->
                        <v-col cols="12" md="3" style="position: relative;">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            Clínica veterinaria
                            <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                          </p>
                          <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                            <!-- Lista con los nombres encontrados mientras se escribe (incluye la razón
                                 social si la clínica tiene dos, 072); se puede escribir uno nuevo. -->
                            <v-combobox
                              v-model="internalClinicName"
                              :search-input.sync="clinicNameSearchInput"
                              :items="clinicNameOptions"
                              :loading="clinicSearchLoading"
                              no-filter
                              flat
                              solo
                              hide-details
                              single-line
                              placeholder="Nombre de la clínica"
                              class="custom-placeholder"
                              style="margin: 0px; padding: 5px 0; font-size: 14px;"
                              autocomplete="off"
                              @change="onClinicNameCommit"
                            >
                              <template v-slot:no-data>
                                <v-list-item dense>
                                  <v-list-item-subtitle>{{ clinicNameNoDataText }}</v-list-item-subtitle>
                                </v-list-item>
                              </template>
                            </v-combobox>
                          </div>
                          <!-- 072-clinic-rut-multiple-names: classify a genuinely new second name -->
                          <v-radio-group
                            v-if="needsClinicNameTypeSelection"
                            v-model="internalClinicNameType"
                            row
                            hide-details
                            style="margin: 4px 0 0 0;"
                          >
                            <v-radio label="Nombre de clínica" value="trade" />
                            <v-radio label="Razón social" value="business" />
                          </v-radio-group>
                        </v-col>

                        <!-- Región/Distrito de clínica -->
                        <v-col cols="12" md="3">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            Región / Distrito de clínica
                            <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                          </p>
                          <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                            <v-text-field
                              v-model="internalClinicCounty"
                              flat
                              solo
                              hide-details
                              single-line
                              placeholder="Región o distrito"
                              class="custom-placeholder"
                              style="margin: 0px; padding: 5px 0; font-size: 14px;"
                            />
                          </div>
                        </v-col>

                        <!-- RUT/DNI de clínica -->
                        <v-col cols="12" md="3">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            RUT / DNI de clínica
                            <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                          </p>
                          <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                            <!-- RUTs de la clínica elegida, o de las encontradas; se puede escribir uno nuevo. -->
                            <v-combobox
                              v-model="internalClinicRut"
                              :search-input.sync="clinicRutSearchInput"
                              :items="clinicRutOptions"
                              flat
                              solo
                              hide-details
                              single-line
                              placeholder="RUT o DNI"
                              class="custom-placeholder"
                              style="margin: 0px; padding: 5px 0; font-size: 14px;"
                              autocomplete="off"
                              @blur="formatClinicRutField"
                            >
                              <template v-slot:no-data>
                                <v-list-item dense>
                                  <v-list-item-subtitle>Elige o busca una clínica para ver sus RUT</v-list-item-subtitle>
                                </v-list-item>
                              </template>
                            </v-combobox>
                          </div>
                        </v-col>

                        <!-- País de clínica -->
                        <v-col cols="12" md="3">
                          <p
                            style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                          >
                            País de clínica
                          </p>
                          <div style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;">
                            <v-select
                              v-model="internalClinicCountryId"
                              :items="countryOptions"
                              :disabled="clinicCountryLocked"
                      @change="formatClinicRutField"
                              flat
                              solo
                              hide-details
                              single-line
                              placeholder="País"
                              style="margin: 0px; padding: 5px 0; font-size: 14px;"
                            />
                          </div>
                          <p
                            v-if="clinicCountryLocked"
                            style="font-size: 11px; color: #6A7682; margin: 4px 0 0 0;"
                          >
                            Se cambia desde el mantenedor de clínicas.
                          </p>
                        </v-col>
                      </v-row>

                      <!-- La ficha de la mascota se crea solo al Aprobar. -->
                      <template v-if="isApprovingRefund">
                        <v-row>
                          <v-col cols="12">
                            <h3 class="font-weight-bold">Registro en ficha de la mascota</h3>
                          </v-col>
                        </v-row>

                        <!-- Sección duplicable -->
                        <div v-for="(entry, index) in entries" :key="index">
                          <v-row v-if="showRadioButtons">
                            <v-col cols="12">
                              <!-- Selección múltiple: una misma atención puede incluir varias vacunas
                                   o ambos tipos de desparasitación. -->
                              <div class="d-flex flex-wrap" style="column-gap: 24px;">
                                <v-checkbox
                                  v-for="option in radioOptions"
                                  :key="option.value"
                                  v-model="entry.selectedRadioOptions"
                                  :label="option.label"
                                  :value="option.value"
                                  hide-details
                                  dense
                                  class="mt-0"
                                ></v-checkbox>
                              </div>
                            </v-col>
                          </v-row>

                          <v-row>
                            <!-- Input de fecha -->
                            <v-col cols="12" md="6">
                              <p
                                style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                              >
                                ¿Cuándo fue usado?
                              </p>
                              <div
                                style="border-radius: 8px; border: solid #CFD3D4 1px; background-color: #EFF2F5; padding: 0 16px; height: 48px; display: flex; align-items: center;"
                              >
                                <div class="d-flex align-center" style="width: 100%;">
                                  <div style="margin-right: 20px;">
                                    <CalendarIcon />
                                  </div>
                                  <div class="flex-row" style="flex: 1;">
                                    <v-text-field
                                      v-model="refundApprovedDate"
                                      readonly
                                      :rules="[requiredRules]"
                                      hide-details="auto"
                                      placeholder="¿Cuándo fue usado?"
                                      class="custom-bg-input"
                                      style="margin: 0; padding: 0; font-size: 14px; background-color: #EFF2F5;"
                                    ></v-text-field>
                                  </div>
                                </div>
                              </div>
                            </v-col>

                            <!-- Frecuencia de postura (opcional) -->
                            <v-col cols="12" md="6">
                              <p
                                style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                              >
                                Frecuencia de postura
                                <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                              </p>
                              <div
                                style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px;"
                              >
                                <v-select
                                  v-model="entry.postureFrequency"
                                  placeholder="Seleccionar frecuencia"
                                  class="custom-placeholder elevation-0 custom-select"
                                  hide-details
                                  flat
                                  solo
                                  style="margin: 0; padding: 5px 0; font-size: 14px; background-color: transparent !important;"
                                  :items="['Diario', 'Semanal', 'Mensual', 'Anual', 'No aplica']"
                                ></v-select>
                              </div>
                            </v-col>
                          </v-row>

                          <!-- Anotaciones para el usuario -->
                          <v-row class="my-4">
                            <v-col cols="12">
                              <p
                                style="font-size: 12px; color: #0F5CB9; margin-bottom: 4px; font-weight: 600;"
                              >
                                Anotaciones para el usuario
                                <span style="font-weight: 300; color: #6A7682;">(opcional)</span>
                              </p>
                              <div
                                style="border-radius: 8px; border: solid #CFD3D4 1px; padding: 4px 16px; width: 100%;"
                              >
                                <v-textarea
                                  v-model="entry.userNotes"
                                  placeholder="Escribe las anotaciones aquí"
                                  class="custom-placeholder"
                                  style="margin: 0; padding: 5px 0; font-size: 14px;"
                                  flat
                                  solo
                                />
                              </div>
                            </v-col>
                          </v-row>
                          <!-- Botón para agregar vacuna o desparasitación -->
                          <v-row v-if="buttonText && entries.length < 2">
                            <v-col cols="12">
                              <v-btn
                                block
                                :style="{ backgroundColor: '#F4F7FA', color: '#000' }"
                                @click="addEntry"
                                elevation="0"
                                class=" custom-button mt-2"
                              >
                                <v-icon left style="color: #6A7682;">mdi-plus</v-icon>
                                <span class="custom-button" style="color: #6A7682; border-radius: 8px;">{{
                                  buttonText
                                }}</span>
                              </v-btn>
                            </v-col>
                          </v-row>
                        </div>
                      </template>
                    </v-form>
                  </v-container>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-list>
        <v-row></v-row>
        <!-- ! -->
        <div style="height: 50px"></div>
        <v-container class="pa-4">
          <v-row justify="center">
            <v-btn
              v-if="can('refund-inbox:review-status') || can('refund-inbox:update')"
              color="primary"
              class="white--text"
              @click="saveRecords"
              style="width: 50%; border-radius: 10px;"
            >
              Guardar registro
            </v-btn>
          </v-row>
        </v-container>
        <div style="height: 20px"></div>
      </v-card>
    </v-dialog>
    <!-- Aviso: se muestra sobre el reembolso abierto, sin cerrarlo, para poder corregir. -->
    <v-snackbar v-model="updateChangelogError" :timeout="8000" color="red" top>
      {{ updateChangelogErrorMsg }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="updateChangelogError = false">
          OK
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar v-model="updateChangelogOk" :timeout="5000">
      {{ updateChangelogOkMsg }}
      <template v-slot:action="{ attrs }">
        <v-btn color="green" text v-bind="attrs" @click="updateChangelogOk = false">
          OK
        </v-btn>
      </template>
    </v-snackbar>
    <overlay :value="loadingOverlay"></overlay>

    <!-- [NUEVO] Modales de sospecha — usuario y clínica por separado (019-mark-suspicious-entities-ui, US4/US5) -->
    <SuspiciousFlagModal
      v-model="showSuspiciousUserModal"
      :rut="user && user.dni"
      entity-type="user"
      :entity-label="(user && user.fullname) || 'este usuario'"
      :can-flag="canFlagSuspicious"
      @saved="onSuspiciousUserSaved"
    />
    <SuspiciousFlagModal
      v-model="showSuspiciousClinicModal"
      :rut="dataRut && dataRut.sellerRut"
      entity-type="clinic"
      entity-label="esta clínica"
      :can-flag="canFlagSuspicious"
      @saved="onSuspiciousClinicSaved"
    />
  </v-row>
</template>

<script>
import RefundServices, { refundErrorMessage } from '../services/refunds.service';
import Overlay from '../components/Overlay.vue';
import InfoCard from './InfoCard.vue';
import MiscUtils from '../utils/misc.utils';
import CalendarIcon from '../assets/calendar-icon.vue';
import DateUtils from '../utils/date.utils';
import CurrencyUtils from '../utils/currency.utils';
import DonGrafServices from '../services/dongraf.services';
import Api360Service from '../services/360.service.js'; // [NUEVO]
import SuspiciousFlagModal from './SuspiciousFlagModal.vue'; // [NUEVO]
import SuspiciousFlagsService from '../services/suspicious-flags.service'; // [NUEVO]
import PetsService from '../services/pets.service.js';
import RespuestasTipoService from '../services/respuestasTipo.service';
import CountryService from '../services/countries.service';
import RutUtils from '../utils/rut.utils';
import { refundStatusColorsMixin } from '../mixins/refundStatusColorsMixin';
import { can } from '../utils/permissions';
import {
  REFUND_STATUS,
  isStatusVisible,
  statusDisabledReason
} from '../utils/refund-status.utils';
import {
  DON_GRAF_RISK_LEVELS,
  DON_GRAF_RISK_SIGNALS,
  DON_GRAF_RISK_SIGNAL_FALLBACK_LABEL,
  DON_GRAF_INTEGRITY_CLEAN_OBSERVATION,
  DON_GRAF_INTEGRITY_UNEVALUATED_PREFIX
} from '../utils/constants';
export default {
  mixins: [refundStatusColorsMixin],
  components: {
    Overlay,
    InfoCard,
    CalendarIcon,
    SuspiciousFlagModal
  },
  data() {
    return {
      requiredRules: v => !!v || 'Este campo es obligatorio',
      activePanel: null,
      changelogFormActivated: false,
      refundData: null,
      userComments: null,
      user: null,
      planData: null,
      petData: null,
      service: null,
      files: null,
      changelogs: [],
      userBankDetails: null,
      coTutors: [],
      petImageUrl: null,
      inputObservations: '',
      paidAt: '',
      respuestasTipo: [],
      refundAmount: '',
      serviceDeductibleApplied: '',
      // Panel abierto de "Registro aprobado del reembolso": 0 = abierto, undefined = colapsado.
      approvalRecordPanel: undefined,
      selectedOption: {
        text: '',
        value: 0,
        color: '',
        icon: '',
        adviceMsg: ''
      },
      loadingOverlay: false,
      dialog: false,
      dataRut: [],
      documentHashAnalysis: null,
      riskScoreResult: null,
      metadataResults: [],
      notifications: false,
      sound: true,
      widgets: false,
      updateChangelogError: false,
      updateChangelogErrorMsg: '',
      updateChangelogOk: false,
      updateChangelogOkMsg: '',
      blacklistInfo: null, // [NUEVO]
      showSuspiciousUserModal: false, // [NUEVO]
      showSuspiciousClinicModal: false, // [NUEVO]
      suspiciousUserInfo: null, // [NUEVO]
      suspiciousClinicInfo: null, // [NUEVO]
      declaracionJurada: null,
      canSeePersonalData: can('refund-inbox:view-personal-data'),
      canSeeAmounts: can('refund-inbox:view-amounts'),
      canSeeAttachments: can('refund-inbox:view-attachments'),
      canSeeBankData: can('refund-inbox:view-bank-data'),
      canSeeClinical: can('refund-inbox:view-clinical'),
      canSeeFraud: can('refund-inbox:view-fraud'),
      canFlagSuspicious: can('refund-inbox:view-fraud') && can('refund-inbox:flag-suspicious'),
      // El registro aprobado es un formulario: solo para quien edita o revisa el reembolso.
      canEditApprovalRecord: can(['refund-inbox:update', 'refund-inbox:review-status']),
      // Sin `color`: lo aporta refundStatusColorsMixin desde la BD.
      statuses: [
        {
          text: 'Registrado',
          value: 0,
          icon: 'mdi-clock-time-nine-outline',
          adviceMsg: 'Solicitud enviada por el usuario, aún no revisada por un admin'
        },
        {
          text: 'En revisión',
          value: 100,
          icon: 'mdi-clock-time-nine-outline',
          adviceMsg: 'Un administrador revisará la solicitud actual'
        },
        {
          text: 'Corregir',
          value: 200,
          icon: 'mdi-account-edit',
          adviceMsg: 'El usuario deberá actualizar la información de su solicitud'
        },
        {
          text: 'Rechazar',
          value: 300,
          icon: 'mdi-close-thick',
          adviceMsg: 'Se rechaza la solicitud de reembolso'
        },
        {
          text: 'Aprobar',
          value: 400,
          icon: 'mdi-check-bold',
          adviceMsg: 'El monto de reembolso debe ser abonado en la cuenta del usuario'
        },
        // 040-auxilia-refund-approval: pasos previos a Aprobar en el flujo Auxilia.
        // Solo se muestran cuando el backend marca el reembolso (visibleStatuses).
        {
          text: 'Aprobado por VetOps',
          value: REFUND_STATUS.VETOPS_APPROVED,
          icon: 'mdi-stethoscope',
          adviceMsg: 'VetOps revisó y aprobó el caso; falta la aprobación de Auxilia'
        },
        {
          text: 'Aprobado por Auxilia',
          value: REFUND_STATUS.AUXILIA_APPROVED,
          icon: 'mdi-shield-check',
          adviceMsg: 'Auxilia aprobó el caso; ya se puede aprobar el reembolso'
        },
        {
          text: 'Pagado',
          value: 500,
          icon: 'mdi-cash-check',
          adviceMsg: 'El reembolso fue efectivamente pagado al usuario'
        },
        {
          text: 'Pago inválido',
          value: 600,
          icon: 'mdi-cash-remove',
          adviceMsg:
            'El banco rechazó la transferencia por datos bancarios erróneos. Queda fuera de las próximas nóminas hasta corregir los datos, y el paso a Pagado deberá hacerse a mano'
        }
      ],
      refundApprovedDate: '',
      refundAnnotations: '',
      internalClinicName: '',
      internalClinicCounty: '',
      internalClinicRut: '',
      // País de la clínica: el suyo si ya tiene, si no el del reembolso.
      internalClinicCountryId: null,
      // La clínica vinculada ya tiene país: se cambia desde el mantenedor de clínicas.
      clinicCountryLocked: false,
      countryOptions: [],
      // 067-clinic-multi-rut: clinic name autocomplete + RUT auto-resolution
      clinicNameSuggestions: [], // [{ clinicId, name, ruts, tradeName?, businessName? }]
      clinicSearchLoading: false,
      clinicSearchTimer: null,
      matchedClinicRuts: [], // RUTs on file for the clinic matching internalClinicName, [] if none/new
      // 072-clinic-rut-multiple-names: RUT-to-name auto-fill + second-name classification
      matchedClinicNames: [], // [businessName, tradeName] when the typed RUT has both on file, [] otherwise
      // Separate from internalClinicName on purpose: binding v-model and
      // search-input.sync to the SAME variable on a Vuetify 2 v-combobox
      // makes them fight each other and search-input stops firing live
      // (only updates on blur) — see the watcher below for how they're kept
      // in sync without that conflict.
      clinicNameSearchInput: '',
      // Lo que se va escribiendo en el combobox del RUT (ver clinicNameSearchInput).
      clinicRutSearchInput: '',
      // Clínica conocida de la que salieron el RUT y el distrito rellenados;
      // null mientras el nombre no coincide con ninguna.
      lastMatchedClinic: null,
      rutLookupTimer: null,
      needsClinicNameTypeSelection: false, // true after a CLINIC_RUT_NAME_TYPE_REQUIRED rejection
      internalClinicNameType: null, // 'trade' | 'business', only sent when needsClinicNameTypeSelection is true
      internalCircumstance: '',
      internalInjuryType: '',
      internalEventDescription: '',
      isFraudulent: false,
      fraudClassification: '',
      isClinicFraudulent: false,
      userExpense: '',
      petRecordDate: '',
      postureFrequency: null,
      userNotes: '',
      menuRefundApprovedDate: false,
      menuPetRecordDate: false,
      showRadioButtons: false,
      radioOptions: [],
      selectedRadioOption: null,
      buttonText: '',
      entries: [
        {
          selectedRadioOptions: [],
          refundApprovedDate: '',
          menuRefundApprovedDate: false,
          postureFrequency: null,
          userNotes: ''
        }
      ]
    };
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    refreshParent: {
      type: Function,
      required: true
    },
    buttonColor: {
      type: String,
      default: 'green'
    },
    externalLabel: {
      type: String,
      default: null
    },
    buttonTextColor: {
      type: String,
      default: '#1E40AF'
    },
    // Abre la ficha apenas se monta: lo usa el enlace «?refund=» de la bandeja.
    autoOpen: {
      type: Boolean,
      default: false
    }
  },

  mounted() {
    if (this.autoOpen) {
      this.dialog = true;
      this.$emit('auto-opened');
    }
  },

  created() {
    this.loadRefundStatusColors();
  },
  methods: {
    can,
    /**
     * True cuando este reembolso es el primero que el usuario (userId, cruzando
     * todas sus mascotas/membresías) ha solicitado. El cálculo real vive en el
     * backend (pawer-backend, spec 090-first-refund-flag) y ya viaja en el prop
     * `data` (la fila del listado que abrió este detalle) — no se recalcula ni
     * se refetchea acá.
     */
    isUsersFirstRefund() {
      return Boolean(this.data && this.data.isFirstRefund === true);
    },
    formatIntegrityObservation(observation) {
      if (!observation) return { kind: 'unevaluated', lines: [] };
      if (observation === DON_GRAF_INTEGRITY_CLEAN_OBSERVATION) {
        return { kind: 'clean', lines: [] };
      }
      if (observation.startsWith(DON_GRAF_INTEGRITY_UNEVALUATED_PREFIX)) {
        return { kind: 'unevaluated', lines: [] };
      }
      return { kind: 'findings', lines: observation.split('\n').filter(Boolean) };
    },
    buildIntegrityDetails(details) {
      if (!details) return null;
      const exifRows = [];
      if (details.exif?.evaluable) {
        if (details.exif.creationDate) {
          exifRows.push({
            label: 'Creación',
            value: DateUtils.formatDateTime(details.exif.creationDate)
          });
        }
        if (details.exif.modDate) {
          exifRows.push({
            label: 'Modificación',
            value: DateUtils.formatDateTime(details.exif.modDate)
          });
        }
        if (details.exif.creator) {
          exifRows.push({ label: 'Creador', value: details.exif.creator });
        }
        if (details.exif.producer) {
          exifRows.push({ label: 'Productor', value: details.exif.producer });
        }
        // fingerprintCount y software son mutuamente excluyentes según el formato del documento.
        if (details.exif.fingerprintCount != null) {
          exifRows.push({
            label: 'Id interno',
            value: String(details.exif.fingerprintCount)
          });
        }
        if (details.exif.software) {
          exifRows.push({ label: 'Software', value: details.exif.software });
        }
        exifRows.push({
          label: 'Firma digital',
          value: details.exif.hasDigitalSignature ? 'Sí' : 'No'
        });
      }
      const c2paRows = [];
      if (details.c2pa?.evaluable) {
        if (details.c2pa.actions?.length) {
          c2paRows.push({ label: 'Acciones', value: details.c2pa.actions.join(', ') });
        }
        if (details.c2pa.digitalSourceType) {
          c2paRows.push({ label: 'Tipo de origen digital', value: details.c2pa.digitalSourceType });
        }
        if (!details.c2pa.hasC2pa) {
          c2paRows.push({ label: 'Manifest C2PA', value: 'No presente' });
        }
      }
      return {
        exif: { evaluable: !!details.exif?.evaluable, rows: exifRows },
        c2pa: { evaluable: !!details.c2pa?.evaluable, rows: c2paRows }
      };
    },
    displayRelacion(type) {
      if (!type) return '—';
      const stripped = type.replace(/^Es mi\s+/i, '');
      return stripped.charAt(0).toUpperCase() + stripped.slice(1);
    },
    /**
     * Abre en otra pestaña el reembolso donde ya se usó el documento duplicado,
     * con el enlace directo de la bandeja: así no se pierde el que se está revisando.
     */
    openDuplicateRefund(referenceIndex) {
      const { href } = this.$router.resolve({
        path: '/refunds/new',
        query: { refund: String(referenceIndex) }
      });
      window.open(href, '_blank', 'noopener');
    },
    formatPetAge(age) {
      if (age == null || age === '') return '-';
      const numAge = parseFloat(age);
      if (isNaN(numAge)) return age;
      const years = Math.floor(numAge);
      const months = Math.round((numAge - years) * 12);
      if (years === 0) return `${months} meses`;
      if (months === 0) return `${years} años`;
      return `${years} años ${months} meses`;
    },
    formatNumber(num) {
      if (!num) return '';
      if (this.wholeAmounts) {
        return `${this.currencySymbol}${CurrencyUtils.formatPriceFromNumber(
          CurrencyUtils.cleanCurrencyFormat(num)
        )}`;
      }
      return `${this.currencySymbol}${CurrencyUtils.formatDecimalPrice(num)}`;
    },
    // Chile bills in whole CLP; Perú and México allow 2 decimals.
    sanitizeAmountInput(value) {
      const raw = String(value ?? '');
      return this.wholeAmounts
        ? CurrencyUtils.cleanCurrencyFormat(raw)
        : CurrencyUtils.cleanDecimalCurrencyFormat(raw);
    },
    amountToNumber(value) {
      const parsed = parseFloat(this.sanitizeAmountInput(value));
      return isNaN(parsed) ? 0 : parsed;
    },
    /** True cuando el monto a reembolsar respeta el tope por evento y el monto disponible. */
    isRefundAmountWithinCap() {
      if (this.refundAmountCap === null) return true;
      return this.amountToNumber(this.refundAmount) <= this.refundAmountCap;
    },
    parseServiceRecordData() {
      return {
        membershipId: this.petData.membershipId,
        servicePlanId: this.service.servicePlanId,
        userId: this.refundData.refund.userId,
        refundRequestId: this.refundData.refund.id,
        description: this.refundAnnotations,
        refundable: true,
        refundedAmount: this.amountToNumber(this.refundAmount),
        userSpends: this.amountToNumber(this.userExpense),
        usedAt: this.refundApprovedDate,
        operationType: 'refund'
      };
    },
    parseDigitalRecord() {
      let digitalRecords = [];
      this.entries.map(entry => {
        digitalRecords.push({
          title: this.service.name,
          userDescription: entry.userNotes,
          adminDescription: `${this.service.name} - ${(entry?.selectedRadioOptions || []).join(
            ', '
          ) || 'sin tipo'} - ${entry?.postureFrequency ?? 'sin frecuencia'} - ${
            this.refundAnnotations
          }`,
          date: this.refundApprovedDate,
          petId: this.refundData.refund.petId,
          expenses: this.amountToNumber(this.userExpense)
        });
      });
      return digitalRecords;
    },
    parseDate(date) {
      return DateUtils.formatToChileanDate(date);
    },
    // --- 067-clinic-multi-rut: clinic name autocomplete + RUT auto-resolution ---
    debouncedSearchClinics(query) {
      clearTimeout(this.clinicSearchTimer);
      this.clinicSearchTimer = setTimeout(() => this.searchClinicsByName(query), 400);
    },
    async searchClinicsByName(query) {
      const q = String(query || '').trim();
      if (q.length < 2) {
        this.clinicNameSuggestions = [];
        return;
      }
      this.clinicSearchLoading = true;
      this.clinicNameSuggestions = await RefundServices.searchClinics(q);
      this.clinicSearchLoading = false;
    },
    // Defensive fallback for @change/@keyup.enter: some browsers' native
    // autofill fills this field without firing the "input" event v-model
    // relies on, so the RUT never resolves until something else (blur,
    // change, Enter) forces a sync — this re-runs the same resolution
    // explicitly whichever event actually fired.
    async onClinicNameCommit() {
      await this.searchClinicsByName(this.internalClinicName);
      this.resolveMatchedClinicRuts();
    },
    // Resolves matchedClinicRuts from the last fetched suggestions whenever the
    // typed name lands on a known clinic — auto-fills the RUT field when that
    // clinic has exactly one on file (FR-005a/FR-005b).
    resolveMatchedClinicRuts() {
      // internalClinicName is expected to always be a string, but is coerced
      // defensively here in case a stale non-string value ever lingers in
      // this shared field (e.g. across a hot-reloaded dev session).
      const typed = String(this.internalClinicName || '').trim().toLowerCase();
      // Elegir la razón social o el nombre de fantasía (072) también cuenta.
      const matched = this.clinicNameSuggestions.find(clinic =>
        [clinic.name, clinic.tradeName, clinic.businessName]
          .filter(Boolean)
          .some(name => name.trim().toLowerCase() === typed)
      );

      if (!matched) {
        this.matchedClinicRuts = [];
        this.clearValuesFromPreviousClinic();
        return;
      }

      // Cambio de una clínica conocida a otra: el RUT y el distrito pasan a ser
      // los de la nueva. La primera coincidencia (p. ej. al abrir un reembolso
      // ya registrado) solo completa lo que esté vacío.
      const switchedClinic =
        this.lastMatchedClinic !== null &&
        this.lastMatchedClinic.clinicId !== matched.clinicId;
      this.lastMatchedClinic = matched;

      this.matchedClinicRuts = matched.ruts;
      // Default to the first RUT on file even with several — the field
      // should never sit empty after a clinic is matched; the operator can
      // still switch to another option or type a new one (FR-005a/FR-005b).
      // 072-clinic-rut-multiple-names: unless the operator already typed/picked
      // a RUT that's itself one of the known options — don't clobber it (this
      // also keeps the RUT-to-name lookup below from fighting this watcher).
      if (!this.isClinicRutAmong(matched.ruts)) {
        this.internalClinicRut = this.formatClinicRut(matched.ruts[0]);
      }
      if (switchedClinic) {
        this.internalClinicCounty = matched.county || '';
      } else {
        this.fillClinicCounty(matched.county);
      }
    },
    /**
     * El nombre dejó de coincidir con la clínica rellenada antes: limpia el RUT
     * y el distrito solo si siguen siendo los de esa clínica, para no dejar
     * datos de otra clínica ni borrar lo que el operador escribió a mano.
     */
    clearValuesFromPreviousClinic() {
      const previous = this.lastMatchedClinic;
      if (!previous) return;
      this.lastMatchedClinic = null;
      if (this.isClinicRutAmong(previous.ruts || [])) {
        this.internalClinicRut = '';
      }
      if (previous.county && this.internalClinicCounty === previous.county) {
        this.internalClinicCounty = '';
      }
    },
    /** El RUT escrito es uno de estos, sin importar puntos ni guion. */
    isClinicRutAmong(ruts) {
      const typed = RutUtils.normalizeIdentifier(this.internalClinicRut);
      return Boolean(typed) && ruts.some(rut => RutUtils.normalizeIdentifier(rut) === typed);
    },
    // El RUT se guarda sin formato (lo limpia el backend); acá se muestra con el
    // formato del país de la clínica (RUT chileno con puntos y guion, RUC tal cual).
    formatClinicRut(rut) {
      const country = this.countryOptions.find(c => c.value === this.internalClinicCountryId);
      return RutUtils.formatClinicTaxId(rut, country && country.text);
    },
    formatClinicRutField() {
      if (String(this.internalClinicRut || '').trim()) {
        this.internalClinicRut = this.formatClinicRut(this.internalClinicRut);
      }
    },
    /**
     * Rellena la región/distrito con el último registrado para la clínica en
     * otros reembolsos (la clínica no guarda uno propio). Nunca pisa lo que el
     * operador ya escribió.
     */
    fillClinicCounty(county) {
      if (county && !String(this.internalClinicCounty || '').trim()) {
        this.internalClinicCounty = county;
      }
    },
    // --- 072-clinic-rut-multiple-names: RUT-to-name auto-fill ---
    debouncedLookupClinicByRut(rut) {
      clearTimeout(this.rutLookupTimer);
      this.rutLookupTimer = setTimeout(() => this.lookupClinicByRut(rut), 400);
    },
    async lookupClinicByRut(rut) {
      const value = String(rut || '').trim();
      if (!value) {
        this.matchedClinicNames = [];
        return;
      }

      const clinic = await RefundServices.getClinicByRut(value);
      if (!clinic) {
        this.matchedClinicNames = [];
        return;
      }
      this.fillClinicCounty(clinic.county);

      if (clinic.tradeName && clinic.businessName) {
        // Razón social preselected by default (FR-007) — never hides the
        // trade name, offered as a switchable option via the combobox below.
        this.matchedClinicNames = [clinic.businessName, clinic.tradeName];
        if (!this.matchedClinicNames.includes(this.internalClinicName)) {
          this.internalClinicName = clinic.businessName;
        }
        return;
      }

      this.matchedClinicNames = [];
      if (!this.internalClinicName) {
        this.internalClinicName = clinic.name;
      }
    },
    async watchData() {
      let refund = this.data;

      if (!this.isRefundAmountWithinCap()) {
        this.showChangelogError(this.refundAmountCapMessage);
        return;
      }

      if (!this.data.changelogs || !this.data.changelogs.status) {
        this.loadingOverlay = false;
        return;
      }

      let lastChangelogStatus = this.data.changelogs.status;
      this.loadingOverlay = true;

      if (lastChangelogStatus.sequenceOrder === this.selectedOption.value) {
        this.loadingOverlay = false;
        return;
      }

      try {
        await RefundServices.updateChangelogStatus({
          refundId: refund.id,
          changelogStatus: this.selectedOption.value,
          observations: this.inputObservations,
          approvedAmount: this.amountToNumber(this.refundAmount),
          serviceDeductibleApplied: this.amountToNumber(this.serviceDeductibleApplied),
          internalClinicName: this.internalClinicName,
          internalClinicCounty: this.internalClinicCounty,
          internalClinicRut: this.internalClinicRut,
          internalClinicCountryId: this.internalClinicCountryId,
          nameType: this.internalClinicNameType,
          internalCircumstance: this.internalCircumstance,
          internalInjuryType: this.internalInjuryType,
          internalEventDescription: this.internalEventDescription,
          isFraudulent: this.isFraudulent,
          fraudClassification: this.fraudClassification,
          isClinicFraudulent: this.isClinicFraudulent,
          paidAt: this.selectedOption.value === 500 ? this.paidAt || null : null
        });

        this.updateChangelogOk = true;
        this.updateChangelogOkMsg = `Actualizado de "${lastChangelogStatus.name}" a "${this.selectedOption.text}"`;

        this.loadingOverlay = false;
        this.dialog = false;
        this.inputObservations = '';
        this.paidAt = '';
        this.refreshParent();
      } catch (error) {
        // 072-clinic-rut-multiple-names: this specific code means the RUT is
        // known under one name and the typed one is a genuine second name —
        // reveal the trade-name/razón-social selector so the operator can
        // classify it and resubmit, instead of just showing the message.
        if (error?.response?.data?.code === 'CLINIC_RUT_NAME_TYPE_REQUIRED') {
          this.needsClinicNameTypeSelection = true;
        }
        this.showChangelogError(
          refundErrorMessage(error, 'Ocurrió un error al intentar actualizar el estado')
        );
      }
    },
    parseAmount(v) {
      let f1 = v.replaceAll('.', '');
      let f = f1.split('').reverse();
      let value = [];
      let tmp = 1;
      let aux = 0;
      while (aux < f.length) {
        value.splice(0, 0, f[aux]);
        if (tmp % 3 == 0 && aux < f1.length - 1) {
          value.splice(0, 0, '.');
          tmp = 1;
        } else tmp++;
        aux++;
      }
      this.refundAmount = value.join('');
    },
    async fetchRefundDetails() {
      this.loadingOverlay = true;
      try {
        const [data, respuestasRes, donGrafData, countriesRes] = await Promise.all([
          RefundServices.getRefundDetails(this.data.id),
          RespuestasTipoService.getAll(),
          // Sin view-fraud el análisis ni se pide.
          this.canSeeFraud ? DonGrafServices.get(this.data.id).catch(() => null) : null,
          CountryService.getAllCountries(true).catch(() => null)
        ]);
        this.countryOptions = (countriesRes?.data || []).map(c => ({ text: c.name, value: c.id }));
        this.refundData = data;
        this.user = data?.user || null;
        this.planData = data?.plan || null;
        this.petData = data?.pet || null;
        this.userBankDetails = data?.userBankDetails || null;
        this.files = data?.files || null;
        this.changelogs = data?.changelogs || [];
        this.service = data?.service || null;
        this.userComments = data?.refund.additionalComment;
        this.declaracionJurada = data?.refund?.declaracionJurada || null;
        this.applyReviewRecord(data?.reviewRecord);
        if (respuestasRes.status === 'ok') this.respuestasTipo = respuestasRes.data;
        this.updateButtonText();

        if (donGrafData?.rawResult) {
          const raw = donGrafData.rawResult;
          this.documentHashAnalysis = raw.documentHashAnalysis || null;
          this.dataRut = raw.billingData?.rut || null;
          // [NUEVO] RiskScore y Metadata aún pueden no venir del backend (ver
          // specs/018-don-graf-metadata-risk/contracts/dongraf-get-response.md) —
          // se leen de forma defensiva y las secciones que dependen de ellos
          // simplemente no se muestran cuando faltan.
          this.riskScoreResult = raw.RiskScore || null;
          this.metadataResults = raw.Metadata?.results || [];
        }

        const token = JSON.parse(localStorage.getItem('accessToken'));
        if (this.canSeeFraud) await this.loadFraudAlerts(token);

        // Consultar estado de identidad
        /*try {
          const pgId = this.user?.pgId;
          if (pgId) {
            const identityRes = await UsersService.getIdentityStatus(pgId);
            if (identityRes.status === 'ok') {
              const { identityValidatedAt, dniReviewRequestedAt } = identityRes.data;
              this.identityPendingReview = !!dniReviewRequestedAt && !identityValidatedAt;
            }
          }
        } catch (e) {
          this.identityPendingReview = false;
        }*/

        // Obtener co-tutores del usuario
        try {
          const pgId = this.user?.pgId;
          if (this.canSeePersonalData && pgId) {
            const coTutorsResponse = await Api360Service.getCoTutors(pgId, token);
            this.coTutors = coTutorsResponse || [];
          }
        } catch (e) {
          this.coTutors = [];
        }

        // Obtener imagen de la mascota via getPetsByUser
        try {
          if (this.user?.pgId) {
            const petsRes = await PetsService.getPetsByUser(this.user.pgId);
            if (petsRes.status === 'ok') {
              const petName = this.petData?.name?.toLowerCase();
              const matched = (petsRes.data || []).find(p => p.name?.toLowerCase() === petName);
              this.petImageUrl = matched?.imageUrl || null;
            }
          }
        } catch (e) {
          this.petImageUrl = null;
        }
      } catch (error) {
        this.updateChangelogError = true;
        this.updateChangelogErrorMsg = 'Ocurrió un error al obtener la data';
      } finally {
        this.loadingOverlay = false;
      }
    },
    /**
     * Lista negra y sospecha del usuario y de la clínica (019-mark-suspicious-entities-ui).
     * Solo se consultan con refund-inbox:view-fraud.
     */
    async loadFraudAlerts(token) {
      if (this.user?.dni) {
        try {
          const blacklistData = await Api360Service.getBlacklist(this.user.dni, token);
          this.blacklistInfo = blacklistData?.isBlacklisted
            ? { reason: blacklistData.blacklist?.reason || null }
            : null;
        } catch (error) {
          this.blacklistInfo = null;
        }
      }
      await this.loadSuspiciousUserStatus();
      await this.loadSuspiciousClinicStatus();
    },
    async updateRefundStatus() {
      // Errors are intentionally left to propagate to saveRecords()'s own
      // try/catch, which sets the user-facing message — catching here too
      // would swallow the real error and always show a generic message.
      await RefundServices.updateChangelogStatus({
        refundId: this.refundData.refund.id,
        changelogStatus: this.selectedOption.value,
        observations: this.inputObservations,
        approvedAmount: this.amountToNumber(this.refundAmount),
        serviceDeductibleApplied: this.amountToNumber(this.serviceDeductibleApplied),
        internalClinicName: this.internalClinicName,
        internalClinicCounty: this.internalClinicCounty,
        internalClinicRut: this.internalClinicRut,
        internalClinicCountryId: this.internalClinicCountryId,
        nameType: this.internalClinicNameType,
        internalCircumstance: this.internalCircumstance,
        internalInjuryType: this.internalInjuryType,
        internalEventDescription: this.internalEventDescription,
        isFraudulent: this.isFraudulent,
        fraudClassification: this.fraudClassification,
        isClinicFraudulent: this.isClinicFraudulent
      });
    },
    /**
     * Muestra un aviso de error dejando el reembolso abierto: el usuario corrige
     * el dato en la misma vista en vez de tener que volver a abrirlo.
     */
    /** Motivo por el que no se puede elegir la opción, o null (regla del backend). */
    optionDisabledReason(status) {
      return statusDisabledReason(status.value, {
        allowedNextStatuses: this.refundData?.allowedNextStatuses,
        currentStatus: this.currentStatusValue
      });
    },
    showChangelogError(message) {
      this.updateChangelogError = true;
      this.updateChangelogErrorMsg = message;
      this.loadingOverlay = false;
    },
    /** Monto del formulario como número, o null si el campo está vacío. */
    amountOrNull(value) {
      const clean = this.sanitizeAmountInput(value);
      if (clean === '') return null;
      const parsed = parseFloat(clean);
      return isNaN(parsed) ? null : parsed;
    },
    /**
     * Rellena el formulario con lo ya registrado: el borrador guardado durante la
     * revisión, o el registro de uso si el reembolso ya fue aprobado. El gasto
     * del usuario parte en el monto solicitado mientras no se haya ingresado.
     */
    applyReviewRecord(record) {
      this.lastMatchedClinic = null;
      if (!record) return;
      const userSpends = record.userSpends ?? record.requestedAmount;
      this.refundApprovedDate = record.usedAt || '';
      this.refundAnnotations = record.notes || '';
      this.userExpense = userSpends != null ? this.sanitizeAmountInput(userSpends) : '';
      this.refundAmount =
        record.approvedAmount != null ? this.sanitizeAmountInput(record.approvedAmount) : '';
      this.serviceDeductibleApplied =
        record.serviceDeductibleApplied != null
          ? this.sanitizeAmountInput(record.serviceDeductibleApplied)
          : '';
      this.internalCircumstance = record.internalCircumstance || '';
      this.internalInjuryType = record.internalInjuryType || '';
      this.internalEventDescription = record.internalEventDescription || '';
      this.internalClinicName = record.internalClinicName || '';
      this.internalClinicCounty = record.internalClinicCounty || '';
      this.internalClinicCountryId = record.clinicCountryId || record.refundCountryId || null;
      this.internalClinicRut = this.formatClinicRut(record.internalClinicRut);
      this.clinicCountryLocked = Boolean(record.clinicCountryId);
    },
    buildReviewRecordPayload() {
      return {
        usedAt: this.refundApprovedDate || null,
        userSpends: this.amountOrNull(this.userExpense),
        approvedAmount: this.amountOrNull(this.refundAmount),
        serviceDeductibleApplied: this.amountOrNull(this.serviceDeductibleApplied),
        notes: this.refundAnnotations || null,
        internalCircumstance: this.internalCircumstance || null,
        internalInjuryType: this.internalInjuryType || null,
        internalEventDescription: this.internalEventDescription || null,
        internalClinicName: this.internalClinicName || null,
        internalClinicCounty: this.internalClinicCounty || null,
        internalClinicRut: this.internalClinicRut || null,
        internalClinicCountryId: this.internalClinicCountryId || undefined,
        nameType: this.internalClinicNameType || undefined
      };
    },
    /**
     * Guarda el registro de revisión sin cambiar el estado ni registrar uso.
     * Devuelve false (con el aviso ya mostrado) si el backend lo rechaza.
     */
    async persistReviewRecord() {
      try {
        await RefundServices.saveReviewRecord(
          this.refundData.refund.id,
          this.buildReviewRecordPayload()
        );
        return true;
      } catch (error) {
        // 072-clinic-rut-multiple-names: see the same check in watchData().
        if (error?.response?.data?.code === 'CLINIC_RUT_NAME_TYPE_REQUIRED') {
          this.needsClinicNameTypeSelection = true;
        }
        this.showChangelogError(
          refundErrorMessage(error, 'Ocurrió un error al guardar el registro del reembolso')
        );
        return false;
      }
    },
    /** Mismo estado: solo se guarda el registro, sin changelog, correo ni push. */
    async saveReviewRecordOnly() {
      this.loadingOverlay = true;
      if (!(await this.persistReviewRecord())) return;
      this.updateChangelogOk = true;
      this.updateChangelogOkMsg = 'Registro del reembolso guardado';
      this.loadingOverlay = false;
      this.refreshParent();
      this.dialog = false;
    },
    async saveRecords() {
      if (this.selectedOption.value === this.currentStatusValue) {
        await this.saveReviewRecordOnly();
        return;
      }
      if (!this.isApprovingRefund) {
        // El registro se guarda primero: si falla, el estado no cambia.
        this.loadingOverlay = true;
        if (!(await this.persistReviewRecord())) return;
        await this.watchData();
        return;
      }
      if (!this.$refs.form.validate()) {
        this.showChangelogError('Faltan campos que completar');
        return;
      }
      this.loadingOverlay = true;
      try {
        const approvedRegisterRefund = this.parseServiceRecordData();
        const digitalRecord = this.parseDigitalRecord();
        // El cambio de estado va primero: si el backend lo rechaza, el throw
        // corta acá y no se registra el uso del servicio.
        await this.updateRefundStatus();
        await RefundServices.postRecordAndRefundDetails({
          approvedRegisterRefund,
          digitalRecord
        });
        this.updateChangelogOk = true;
        this.updateChangelogOkMsg = `Estado de reembolso actualizado`;
        this.loadingOverlay = false;
        this.inputObservations = '';
        this.refreshParent();
        this.dialog = false;
      } catch (err) {
        // 072-clinic-rut-multiple-names: see the same check in watchData().
        if (err?.response?.data?.code === 'CLINIC_RUT_NAME_TYPE_REQUIRED') {
          this.needsClinicNameTypeSelection = true;
        }
        this.showChangelogError(
          refundErrorMessage(
            err,
            'Ocurrió un error al intentar actualizar el estado'
          )
        );
      }
    },
    updateRadioOptions() {
      if (!this.service || !this.service.name) {
        this.showRadioButtons = false;
        this.radioOptions = [];
        return;
      }

      const serviceName = this.service.name.toLowerCase();

      if (
        serviceName.includes('vacunas obligatorias') ||
        serviceName.includes('vacunas opcionales')
      ) {
        this.showRadioButtons = true;
        if (this.petData.species === 'dog') {
          this.radioOptions = [
            { label: 'Antirrábica', value: 'antirrabica' },
            { label: 'Séxtuple/Óctuple', value: 'sextuple_octuple' },
            { label: 'KC', value: 'kc' }
          ];
        } else {
          this.radioOptions = [
            { label: 'Antirrábica', value: 'antirrabica' },
            { label: 'Triple felina', value: 'triple_felina' },
            { label: 'FeLV', value: 'felv' }
          ];
        }
      } else if (serviceName.includes('desparasitación')) {
        this.showRadioButtons = true;
        this.radioOptions = [
          { label: 'Externa', value: 'externa' },
          { label: 'Interna', value: 'interna' }
        ];
      } else {
        this.showRadioButtons = false;
        this.radioOptions = [];
      }
    },
    updateButtonText() {
      if (!this.service || !this.service.name) {
        this.buttonText = '';
        return;
      }

      const serviceName = this.service.name.toLowerCase();

      if (serviceName.includes('vacunas')) {
        this.buttonText = 'Agregar vacuna';
      } else if (serviceName.includes('desparasitación')) {
        this.buttonText = 'Agregar desparasitación';
      } else {
        this.buttonText = '';
      }
    },
    onAddButtonClick() {},
    addEntry() {
      this.entries.push({
        selectedRadioOptions: [],
        refundApprovedDate: '',
        menuRefundApprovedDate: false,
        postureFrequency: null,
        userNotes: ''
      });
    },
    async loadSuspiciousUserStatus() {
      if (!this.user?.dni) {
        this.suspiciousUserInfo = null;
        return;
      }
      const result = await SuspiciousFlagsService.lookup(this.user.dni, 'user');
      this.suspiciousUserInfo = result.status === 'ok' && result.data.isSuspicious
        ? result.data.suspiciousFlag
        : null;
    },
    async loadSuspiciousClinicStatus() {
      if (!this.dataRut?.sellerRut) {
        this.suspiciousClinicInfo = null;
        return;
      }
      const result = await SuspiciousFlagsService.lookup(this.dataRut.sellerRut, 'clinic');
      this.suspiciousClinicInfo = result.status === 'ok' && result.data.isSuspicious
        ? result.data.suspiciousFlag
        : null;
    },
    async onSuspiciousUserSaved() {
      await this.loadSuspiciousUserStatus();
      this.updateChangelogOk = true;
      this.updateChangelogOkMsg = 'Sospecha del usuario actualizada correctamente';
    },
    async onSuspiciousClinicSaved() {
      await this.loadSuspiciousClinicStatus();
      this.updateChangelogOk = true;
      this.updateChangelogOkMsg = 'Sospecha de la clínica actualizada correctamente';
    }
  },

  computed: {
    /**
     * Opciones del combobox de clínica: los nombres del RUT ya ingresado (072)
     * y los de las clínicas encontradas mientras se escribe, sin repetir.
     */
    clinicNameOptions() {
      const names = [...this.matchedClinicNames];
      for (const clinic of this.clinicNameSuggestions) {
        names.push(clinic.businessName, clinic.tradeName, clinic.name);
      }
      return [...new Set(names.filter(Boolean))];
    },
    clinicNameNoDataText() {
      const typed = String(this.clinicNameSearchInput || '').trim();
      if (typed.length < 2) return 'Escribe al menos 2 letras para buscar';
      return 'Sin coincidencias: se registrará como clínica nueva';
    },
    /**
     * Opciones del combobox de RUT: los de la clínica elegida o, si aún no hay
     * una, los de las clínicas encontradas.
     */
    clinicRutOptions() {
      const ruts = this.matchedClinicRuts.length
        ? this.matchedClinicRuts
        : this.clinicNameSuggestions.flatMap(clinic => clinic.ruts || []);
      return [...new Set(ruts.map(rut => this.formatClinicRut(rut)))];
    },
    /** Estado actual del reembolso (sequenceOrder del último changelog). */
    currentStatusValue() {
      return this.data?.changelogs?.status?.sequenceOrder ?? null;
    },
    /**
     * Opciones de "Cambiar a": los mismos objetos de `statuses` (el radio
     * compara por referencia), sin 410/420 fuera del flujo Auxilia.
     */
    visibleStatuses() {
      const context = {
        requiresAuxiliaReview: this.refundData?.requiresAuxiliaReview,
        currentStatus: this.currentStatusValue
      };
      return this.statuses.filter(status => isStatusVisible(status.value, context));
    },
    /**
     * Pasar a Aprobado: el único momento en que se registra el uso del servicio
     * y la ficha de la mascota. Si ya estaba aprobado, guardar solo edita.
     */
    isApprovingRefund() {
      return this.selectedOption.value === 400 && this.currentStatusValue !== 400;
    },
    /**
     * Rechazar (300) ya tiene su propio "Registro interno" con estos mismos
     * campos; mostrar ambos duplicaría los inputs.
     */
    showApprovalRecordSection() {
      return this.selectedOption.value !== 300;
    },
    donGrafDuplicateCount() {
      if (!this.documentHashAnalysis?.results) return 0;
      return this.documentHashAnalysis.results.filter(r => r.status === 'duplicate').length;
    },
    donGrafRiskLevelMeta() {
      if (!this.riskScoreResult?.riskLevel) return null;
      return DON_GRAF_RISK_LEVELS[this.riskScoreResult.riskLevel] || null;
    },
    donGrafRiskSignals() {
      if (!this.riskScoreResult?.signals?.length) return [];
      return this.riskScoreResult.signals.map(signal => ({
        code: signal.code,
        label: DON_GRAF_RISK_SIGNALS[signal.code] || DON_GRAF_RISK_SIGNAL_FALLBACK_LABEL
      }));
    },
    donGrafDocuments() {
      if (!this.documentHashAnalysis?.results) return [];
      return this.documentHashAnalysis.results.map(doc => {
        const integrity =
          this.metadataResults.find(m => doc.hash && m.hash === doc.hash) || null;
        const integrityView = integrity
          ? this.formatIntegrityObservation(integrity.observation)
          : null;
        return {
          ...doc,
          integrity,
          integrityView,
          // Solo con hallazgos reales vale la pena desplegar un acordeón — el resto
          // de los casos (limpio, no evaluado, sin integridad) se muestran en línea.
          hasFindings: integrityView?.kind === 'findings',
          integrityDetails: integrity?.details
            ? this.buildIntegrityDetails(integrity.details)
            : null
        };
      });
    },
    userExpenseFormatted: {
      get() {
        return this.formatNumber(this.userExpense);
      },
      set(newValue) {
        this.userExpense = this.sanitizeAmountInput(newValue);
      }
    },
    refundAmountFormatted: {
      get() {
        return this.formatNumber(this.refundAmount);
      },
      set(newValue) {
        this.refundAmount = this.sanitizeAmountInput(newValue);
      }
    },
    /**
     * Tope por evento (monto) del servicio, ya convertido a la moneda del plan por
     * el backend. Null cuando el servicio no tiene tope por evento.
     */
    perEventCap() {
      const cap = Number(this.service?.maxAmountPerEventValue);
      return cap > 0 ? cap : null;
    },
    /**
     * Monto que aún queda del tope anual del servicio (sin contar este mismo
     * reembolso). Null cuando el servicio no tiene tope anual.
     */
    availableAmountCap() {
      const available = this.service?.availableAmountValue;
      if (available === null || available === undefined) return null;
      const value = Number(available);
      return isNaN(value) ? null : value;
    },
    /** True cuando el monto disponible es más restrictivo que el tope por evento. */
    isAvailableAmountTheLimit() {
      if (this.availableAmountCap === null) return false;
      return this.perEventCap === null || this.availableAmountCap < this.perEventCap;
    },
    /** Máximo aprobable: el menor entre el tope por evento y el monto disponible. */
    refundAmountCap() {
      return this.isAvailableAmountTheLimit ? this.availableAmountCap : this.perEventCap;
    },
    /** Aviso en lenguaje humano cuando el monto ingresado supera el máximo aprobable. */
    refundAmountCapMessage() {
      const cap = this.formatNumber(String(this.refundAmountCap));
      if (this.isAvailableAmountTheLimit) {
        return `A este servicio le quedan ${cap} disponibles, así que no puedes aprobar un monto mayor. Ingresa ${cap} o menos.`;
      }
      return `Este servicio cubre como máximo ${cap} por evento, así que no puedes aprobar un monto mayor. Ingresa ${cap} o menos.`;
    },
    /** Regla de v-text-field: el monto a reembolsar no puede superar el máximo aprobable. */
    refundAmountCapRule() {
      return value => {
        if (this.refundAmountCap === null) return true;
        const amount = this.amountToNumber(value);
        if (!amount) return true;
        return amount <= this.refundAmountCap || this.refundAmountCapMessage;
      };
    },
    serviceDeductibleAppliedFormatted: {
      get() {
        return this.formatNumber(this.serviceDeductibleApplied);
      },
      set(newValue) {
        this.serviceDeductibleApplied = this.sanitizeAmountInput(newValue);
      }
    },
    isExpanded() {
      return this.activePanel !== null;
    },
    userInfoItems() {
      if (!this.user) return [];
      const country = {
        label: 'País',
        value: this.user.country
          ? this.user.country
              .split(' ')
              .slice(1)
              .join(' ') || this.user.country
          : '-'
      };
      if (!this.canSeePersonalData) {
        return [{ label: 'Nombre', value: `${this.user.fullname}` }, country];
      }
      const items = [
        { label: 'Nombre', value: `${this.user.fullname}` },
        { label: 'RUT / DNI', value: this.user.dni },
        { label: 'Email', value: this.user.email },
        { label: 'Teléfono', value: this.user.phone },
        country
      ];
      this.coTutors.forEach((coTutor, i) => {
        const label = this.coTutors.length > 1 ? `Co-tutor ${i + 1}` : 'Co-tutor';
        const item = {
          label,
          value: `${
            coTutor.firstName || coTutor.lastName
              ? `${coTutor.firstName || ''} ${coTutor.lastName || ''}`.trim()
              : 'Sin nombre'
          } · ${coTutor.rut || 'Sin RUT'} · ${this.displayRelacion(coTutor.relationshipType) ||
            'Sin relación'}`,
          divider: true
        };
        if (this.canSeeAttachments && coTutor.fileUrl) {
          item.suffixLink = { text: 'Ver documento', url: coTutor.fileUrl };
        }
        items.push(item);
      });
      return items;
    },
    petGridItems() {
      if (!this.petData) return [];
      const species = this.petData.species === 'dog' ? 'Perro' : 'Gato';
      return [
        { label: 'Nombre', value: this.petData.name },
        { label: 'Especie', value: species },
        { label: 'Edad', value: this.formatPetAge(this.petData.age) }
      ];
    },
    petGridItems2() {
      if (!this.petData) return [];
      return [
        { label: 'Raza', value: this.petData.breed },
        { label: 'Alta en Pawer', value: this.petData?.createdAt || '-' },
        { label: 'Referencia', value: this.petData.ref }
      ];
    },
    petInfoItems() {
      return [];
    },
    serviceInfoItems() {
      return [];
    },
    lastServiceTypeLabel() {
      const map = { refund: 'Reembolso', operational: 'Operativo', upsell: 'Upsell' };
      return map[this.service?.lastServiceType] || this.service?.lastServiceType || '-';
    },
    serviceCombinedGridItems() {
      if (!this.service) return [];
      const usedDeductible =
        this.service.deductible === 'No aplica'
          ? 'No aplica'
          : this.formatNumber(
              String(this.refundData?.refund?.serviceDeductibleApplied ?? 0)
            );
      // "Cobertura" siempre en negrita (mismo trato que "Reembolso solicitado" en
      // este mismo bloque) para que el campo se note independiente del valor;
      // el morado es la señal extra cuando específicamente es un tercero.
      const supplierName = this.service.supplier?.name || 'Pawer';
      const isThirdPartySupplier = supplierName !== 'Pawer';
      return [
        { label: 'Reembolso solicitado', value: '-', bold: true },
        { label: 'Deducible aplicado', value: usedDeductible },
        { divider: true },
        { sectionTitle: 'Condiciones del servicio' },
        {
          label: 'Cobertura',
          value: supplierName,
          bold: true,
          color: isThirdPartySupplier ? '#5E35B1' : undefined
        },
        { label: 'Tope (monto)', value: this.service.serviceCap },
        { label: 'Tope (eventos)', value: this.service.maxEvents },
        ...(this.service.maxAmountPerEvent
          ? [{ label: 'Tope por evento (monto)', value: this.service.maxAmountPerEvent }]
          : []),
        { label: 'Monto disponible', value: this.service.availableAmount },
        { label: 'Eventos disponibles', value: this.service.availableEvents || '-' },
        { label: 'Deducible', value: this.service.deductible },
        { label: 'Total utilizado', value: this.service.usedAmount },
        { label: 'Plazo de corrección', value: this.service.refundReviewDays || '-' }
      ];
    },
    attachmentsInfoItems() {
      return [];
    },
    /** Adjuntos (view-attachments) y datos bancarios (view-bank-data), cada uno con su permiso. */
    attachmentsGridItems2() {
      const fileItems = this.attachmentFileItems;
      if (!this.bankDataItems.length) return fileItems;
      return fileItems.length
        ? [...fileItems, { divider: true }, ...this.bankDataItems]
        : this.bankDataItems;
    },
    attachmentFileItems() {
      if (!this.canSeeAttachments || !this.files) return [];
      // Group files by date (createdAt), preserving chronological order
      const groups = [];
      const seen = new Map();
      for (const attachment of this.files) {
        const dateKey = attachment.createdAt || 'Sin fecha';
        if (!seen.has(dateKey)) {
          seen.set(dateKey, []);
          groups.push({ date: dateKey, files: seen.get(dateKey) });
        }
        seen.get(dateKey).push(attachment);
      }
      const items = [];
      for (const group of groups) {
        items.push({ sectionTitle: group.date });
        for (const attachment of group.files) {
          items.push({
            label: attachment.type,
            link: attachment.url,
            // Pasa por el backend para poder convertir fotos HEIC al abrirlas.
            fetchFile: attachment.id
              ? signal => RefundServices.fetchAttachmentFile(attachment.id, signal)
              : null,
            visible: attachment.visible,
            labelLink: true
          });
        }
      }
      if (this.userComments) {
        items.push({ commentText: this.userComments });
      }
      return items;
    },
    bankDataItems() {
      if (!this.canSeeBankData || !this.userBankDetails) return [];
      const items = [
        { label: 'Banco', value: this.userBankDetails.bank },
        { label: 'Tipo de cuenta', value: this.userBankDetails.accountType },
        { label: 'Nro de cuenta', value: this.userBankDetails.accountNumber }
      ];
      if (this.user?.country?.includes('Perú')) {
        items.push({ label: 'CCI', value: this.userBankDetails.cci });
      }
      return items;
    },
    // Símbolo y decimales por país (spec 084): antes todo lo que no era Perú se
    // mostraba en pesos chilenos, México incluido.
    countryMoney() {
      return CurrencyUtils.countryMoney(this.user?.country);
    },
    // Montos enteros (Chile) o con centavos (Perú, México).
    wholeAmounts() {
      return this.countryMoney.decimals === 0;
    },
    currencySymbol() {
      return this.countryMoney.symbol;
    },
    respuestasTipoFiltradas() {
      const map = { 200: 'corregir', 300: 'rechazar', 400: 'aprobar' };
      const cat = map[this.selectedOption?.value];
      if (!cat) return [];
      return this.respuestasTipo.filter(r => r.category === cat);
    },
    membershipInfoItems() {
      return [];
    },
    membershipGridItems() {
      if (!this.planData) return [];

      // Color para Fecha de Carencia
      let graceColor = null;
      const graceDateRaw = this.planData.endSupplierGraceDays;
      if (graceDateRaw) {
        let graceDate;
        if (typeof graceDateRaw === 'string' && graceDateRaw.includes('/')) {
          const [day, month, year] = graceDateRaw.split('/');
          graceDate = new Date(`${year}-${month}-${day}`);
        } else if (typeof graceDateRaw === 'string' && graceDateRaw.includes('-')) {
          graceDate = new Date(graceDateRaw);
        }
        if (graceDate && !isNaN(graceDate.getTime())) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          graceColor = graceDate > today ? '#DC2626' : '#16A34A';
        }
      }

      return [
        { label: 'Empresa', value: '-' },
        ...(this.canSeeAttachments
          ? [
              {
                label: 'Contrato del usuario',
                value: this.planData.contractUrl ? 'Ver contrato' : '-',
                link: this.planData.contractUrl || null
              }
            ]
          : []),
        {
          label: 'Plan',
          value: this.planData.name || '-',
          link: `/business-relationships/plans/${this.planData.id}`
        },
        {
          label: 'Estado',
          value: MiscUtils.parseMembershipStatus(this.petData ? this.petData.status : null)
        },
        { label: 'Inicio de Membresía', value: this.planData.startDate || '-' },
        { label: 'Fecha de Renovación', value: '-' },
        { label: 'Fecha de Carencia', value: graceDateRaw || '-', color: graceColor },
        {
          label: 'Fecha de término',
          value:
            this.planData.endDate && this.planData.endDate.includes('-')
              ? DateUtils.formatToChileanDateV2(this.planData.endDate)
              : 'Plan en vigencia'
        },
        { label: 'Edad límite', value: this.formatPetAge(this.planData.limitYears) },
        { label: 'Canal de adquisición', value: this.planData?.acquisitionChannel || '-' }
      ];
    }
  },
  watch: {
    service: {
      immediate: true,
      handler() {
        this.updateRadioOptions();
        this.updateButtonText();
      }
    },
    dialog(newVal) {
      if (newVal) {
        // Parte en el estado actual: guardar sin elegir otro solo guarda el
        // registro, en vez de devolver el reembolso a "Registrado".
        this.selectedOption =
          this.statuses.find(status => status.value === this.currentStatusValue) ||
          this.selectedOption;
        this.fetchRefundDetails();
      }
    },
    isApprovingRefund: {
      immediate: true,
      handler(approving) {
        this.approvalRecordPanel = approving ? 0 : undefined;
      }
    },
    // 067-clinic-multi-rut: search as the operator types the clinic name,
    // and re-resolve the RUT field whenever the typed name changes.
    internalClinicName() {
      this.debouncedSearchClinics(this.internalClinicName);
      this.resolveMatchedClinicRuts();
      // 072-clinic-rut-multiple-names: a stale second-name classification
      // must not silently apply to a name the operator just changed to.
      this.needsClinicNameTypeSelection = false;
      this.internalClinicNameType = null;
    },
    clinicNameSuggestions() {
      this.resolveMatchedClinicRuts();
    },
    // 072-clinic-rut-multiple-names: resolve known name(s) as the operator
    // types the RUT, symmetric to the name-driven lookup above.
    internalClinicRut(newVal) {
      this.debouncedLookupClinicByRut(newVal);
      this.needsClinicNameTypeSelection = false;
      this.internalClinicNameType = null;
    },
    // Forwards the combobox's live keystrokes (search-input.sync) into
    // internalClinicName, which already reacts on its own watcher above —
    // kept as a separate variable so it doesn't fight v-model (see the data()
    // comment on clinicNameSearchInput for why).
    clinicNameSearchInput(newVal) {
      if (newVal !== null && newVal !== this.internalClinicName) {
        this.internalClinicName = newVal;
      }
    },
    // Same forwarding for the RUT combobox, so the RUT-to-name lookup runs as
    // the operator types instead of only on blur.
    clinicRutSearchInput(newVal) {
      if (newVal !== null && newVal !== this.internalClinicRut) {
        this.internalClinicRut = newVal;
      }
    }
  }
};
</script>

<style>
/* Acotado a .pw-refund-form (content-class del v-dialog): estas reglas tocan
   internals de Vuetify y sin el prefijo se aplicaban a TODA la app, porque el
   router carga las vistas de forma eager y este <style> no lleva scoped. */
.pw-refund-form .theme--light.v-label {
  color: #abafb1;
}

.custom-placeholder input::placeholder {
  color: #abafb1;
  opacity: 1;
  font-size: 14px;
}

.pw-refund-form .v-textarea .v-input__control textarea {
  resize: none !important;
}

.pw-refund-form .v-text-field > .v-input__control > .v-input__slot:after {
  background-color: #eff2f5 !important;
  border-color: transparent;
  border-style: solid;
  border-width: thin 0 thin 0;
  transform: scaleX(0);
}
.pw-refund-form .v-text-field input {
  background-color: transparent !important;
}
.pw-refund-form .theme--light.v-text-field > .v-input__control > .v-input__slot:before {
  border-color: transparent;
}

.pw-refund-form .v-input__slot {
  margin: 0;
}

.custom-select .v-select__slot,
.custom-select .v-input__slot {
  background-color: transparent !important;
  box-shadow: none !important;
}

.custom-datepicker .v-input__control {
  background-color: #eff2f5 !important;
}

.pw-refund-form .v-expansion-panel-content {
  margin-bottom: 16px;
}

.custom-button {
  color: #6a7682 !important;
  font-family: 'Inter', sans-serif !important;
  box-shadow: none !important;
}
.custom-bg-input input {
  background-color: #eff2f5 !important;
}

.pw-refund-form .v-select.v-input--is-focused .v-input__slot,
.pw-refund-form .v-select .v-input__slot {
  background-color: transparent !important;
  box-shadow: none !important;
}
.v-application .pw-refund-form .v-text-field.v-input--is-disabled .v-input__slot {
  background-color: transparent !important;
  box-shadow: none !important;
}

.pw-refund-form .v-input.custom-placeholder.v-text-field--solo .v-input__slot {
  min-height: unset !important;
  margin-bottom: 0 !important;
}

/* [NUEVO] Alerta Lista Negra en modal de reembolsos */
.blacklist-alert-refund {
  background-color: #c62828;
  padding: 10px 24px 10px 28px;
  margin: 0;
}
.blacklist-alert-header {
  display: flex;
  align-items: center;
}
.blacklist-title {
  color: white;
  font-weight: 700;
  font-size: 0.9rem;
}
.blacklist-reason {
  color: white;
  font-weight: 400;
  font-size: 0.78rem;
  margin-top: 2px;
  padding-left: 20px;
}
.suspicious-alert-refund {
  background-color: #B85C00;
  padding: 10px 24px 10px 28px;
  margin: 0;
}
.suspicious-alert-header {
  display: flex;
  align-items: center;
}
.suspicious-title {
  color: white;
  font-weight: 700;
  font-size: 0.9rem;
}
.suspicious-reason-refund {
  color: white;
  font-weight: 400;
  font-size: 0.78rem;
  margin-top: 2px;
  padding-left: 20px;
}
.dj-grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0 16px;
}

.dj-grid-full {
  grid-column: 1 / -1;
}

.dj-section-title {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  font-weight: 600;
  margin-bottom: 12px;
}

.dj-field {
  margin-bottom: 12px;
}

.dj-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 2px;
}

.dj-value {
  font-size: 13px;
  color: #111827;
  font-weight: 500;
}
</style>

<style scoped>
/* 040-auxilia-refund-approval: motivo bajo una opción de estado deshabilitada. */
.status-option {
  display: inline-flex;
  flex-direction: column;
  line-height: 1.2;
}

.status-option__hint {
  font-size: 11px;
  color: var(--pw-text-muted);
}
</style>
